import * as Sentry from "@sentry/react"

import {
  getOrCreateOwnerPortalDeviceId,
  getOwnerPortalAuthorizationHeader,
  getOrCreateRegistrationSessionId,
} from "@/lib/session/public-session"
import { getOperatorAuthorizationHeader } from "@/lib/session/operator-session"
import type {
  SafetyQrKitVariant,
  SafetyQrProductFamily,
  SafetyQrSolutionCategory,
  SafetyQrVehicleClass,
} from "@/lib/safetyqr-catalog"

type ApiAuthMode = "none" | "operator" | "owner"

export type TagType = "fleet_tag" | "personal_tag" | "vehicle_sticker"
export type SubjectAgeBand = "adult_18_plus" | "under_18"

export interface ApiEnvelope<TData> {
  count?: number
  data: TData
  message?: string
  success: boolean
  total?: number
  warning?: string | null
}

export interface ApiFailure {
  code?: string
  details?: unknown
  error?: string
  message?: string
  registrationRequired?: boolean
  resendAfterSec?: number | null
  success?: false
}

export interface BackendHealthResponse {
  status: string
  message: string
  timestamp: string
  environment: string
  database: string
  uploadService: string
}

export interface DeviceSummary {
  createdAt?: string | null
  organizationId?: string | null
  status: string | null
  tag: string
  type: string | null
  updatedAt?: string | null
}

export interface VehicleSummary {
  color?: string | null
  companyName?: string | null
  fleetName?: string | null
  make?: string | null
  model?: string | null
  ownerEmail?: string | null
  ownerName?: string | null
  ownerPhone?: string | null
  plateNumber?: string | null
  type?: string | null
  vin?: string | null
  year?: number | null
}

export interface ContactSummary {
  email: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  name: string | null
  phone: string | null
}

export interface AssignmentSummary {
  activatedAt?: string | null
  assignedAt?: string | null
  assigneeEmail?: string | null
  assigneeName?: string | null
  assigneePhone?: string | null
  assignmentStatus?: string | null
  externalRef?: string | null
  memberType?: string | null
  metadata?: Record<string, unknown> | null
  tagSource?: string | null
}

export interface MedicalProfileSummary {
  allergies: string | null
  bloodType: string | null
  consentAccepted: boolean | null
  medicalConditions: string | null
  medications: string | null
  responderNotes: string | null
}

export interface RegistrationSummary {
  activatedByTag?: string | null
  activatedByTagRole?: string | null
  activatedAt?: string | null
  activationCompletedAt?: string | null
  activationStartedAt?: string | null
  assignedAt?: string | null
  allergies?: string | null
  assigneeEmail?: string | null
  assigneeName?: string | null
  assigneePhone?: string | null
  assignmentStatus?: string | null
  bloodType?: string | null
  companyName?: string | null
  consentAccepted?: boolean | null
  contactEmail?: string | null
  contactName?: string | null
  contactPhone?: string | null
  createdAt?: string | null
  email?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  externalRef?: string | null
  fleetName?: string | null
  fullName?: string | null
  medicalConditions?: string | null
  medications?: string | null
  memberType?: string | null
  metadata?: Record<string, unknown> | null
  notes?: string | null
  organizationId?: string | null
  physicalTagRole?: string | null
  plantationId?: string | null
  plantationName?: string | null
  emergencyContactEmail?: string | null
  ownerEmail?: string | null
  ownerName?: string | null
  ownerPhone?: string | null
  preferredLanguageCode?: string | null
  preferredLanguageLabel?: string | null
  phone?: string | null
  plateNumber?: string | null
  responderNotes?: string | null
  status?: string | null
  stickerId?: string | null
  tag?: string | null
  tagSource?: string | null
  tagType?: TagType | null
  updatedAt?: string | null
  vehicleColor?: string | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vehicleType?: string | null
  vehicleYear?: number | null
  vin?: string | null
  zoneName?: string | null
  zoneNamedAt?: string | null
  compliance?: ComplianceSummary | null
}

export interface ConsentNoticeVersionSummary {
  audience?: string | null
  contentJson?: Record<string, unknown> | null
  id: string
  locale?: string | null
  publishedAt?: string | null
  summary?: string | null
  surface?: string | null
  title?: string | null
  version?: string | null
}

export interface ComplianceSummary {
  activeNoticeVersion?: ConsentNoticeVersionSummary | null
  activeNoticeVersionId?: string | null
  consentStatus?: string | null
  erasureRequestedAt?: string | null
  erasedAt?: string | null
  guardianApprovalStatus?: string | null
  guardianApprovedAt?: string | null
  guardianEmail?: string | null
  guardianName?: string | null
  guardianPhone?: string | null
  guardianRelationship?: string | null
  guardianRequired?: boolean | null
  id?: string | null
  isMinor?: boolean | null
  privacyStatus?: string | null
  registrationStatus?: string | null
  registrantRelationship?: string | null
  stickerId?: string | null
  subjectAgeBand?: SubjectAgeBand | null
  tagType?: TagType | null
  withdrawnAt?: string | null
}

export interface PrivacyRequestRecord {
  acknowledgedAt?: string | null
  createdAt?: string | null
  id: string
  intakeChannel?: string | null
  reason?: string | null
  requesterEmail?: string | null
  requesterName?: string | null
  requesterPhone?: string | null
  requestType?: string | null
  resolvedAt?: string | null
  resolutionSummary?: string | null
  status?: string | null
}

export interface GrievanceCaseRecord {
  assignee?: string | null
  caseType?: string | null
  createdAt?: string | null
  description?: string | null
  id: string
  registrationComplianceId?: string | null
  requesterEmail?: string | null
  requesterName?: string | null
  requesterPhone?: string | null
  slaDueAt?: string | null
  sourceSurface?: string | null
  status?: string | null
  tag?: string | null
}

export interface FleetPilotLeadRecord {
  city?: string | null
  companyName?: string | null
  contactName?: string | null
  createdAt?: string | null
  currentSafetyStack?: string | null
  estimatedVehicleCount?: number | null
  fleetSegment?: string | null
  fleetSizeBand?: string | null
  id: string
  notificationError?: string | null
  notificationStatus?: string | null
  notifiedAt?: string | null
  operatingRegion?: string | null
  organizationId?: string | null
  organizationName?: string | null
  phone?: string | null
  pilotGoals?: string | null
  preferredDemoWindow?: string | null
  rolloutTimeline?: string | null
  sourceSurface?: string | null
  status?: string | null
  updatedAt?: string | null
  workEmail?: string | null
}

export interface FleetAuthorityDirectoryRecord {
  capturedAt?: string | null
  country?: string | null
  coverageLevel?: string | null
  coverageStatus?: string | null
  channelType?: string | null
  channelValue?: string | null
  authorityType?: string | null
  entryModel?: string | null
  id: string
  isFallback?: boolean | null
  isPrimaryForV1?: boolean | null
  notes?: string | null
  regionGroup?: string | null
  routingPriority?: number | null
  sourceUrls?: string[]
  stateOrUt?: string | null
  verificationStatus?: string | null
}

export interface SecurityIncidentRecord {
  affectedSurface?: string | null
  certInStatus?: string | null
  closedAt?: string | null
  createdAt?: string | null
  detectedAt?: string | null
  evidence?: Record<string, unknown> | null
  id: string
  notes?: string | null
  openedAt?: string | null
  organizationId?: string | null
  owner?: string | null
  severity?: string | null
  sourceReference?: string | null
  sourceType?: string | null
  statutoryNoticeStatus?: string | null
  status?: string | null
  updatedAt?: string | null
}

export interface PublicKitSummary {
  activatedByTag?: string | null
  activatedByTagRole?: string | null
  activationState?: "activated" | "registration_required" | null
  code?: string | null
  id: string
  kitVariant?: string | null
  solutionCategory?: string | null
  productFamily?: string | null
  vehicleClass?: string | null
}

export interface PublicPhysicalTagSummary {
  id?: string | null
  placement?: string | null
  postActivationRoutePolicy?: string | null
  preActivationRoutePolicy?: string | null
  primaryCopy?: string | null
  printSlot?: string | null
  reportTemplateId?: string | null
  role?: string | null
  tag: string
  tagSlot?: string | null
  zoneName?: string | null
  zoneNamedAt?: string | null
  zoneNamingState?: "named" | "required" | null
}

export interface PublicPlantationSummary {
  plantationId?: string | null
  plantationName?: string | null
  zoneName?: string | null
  zoneNamedAt?: string | null
  zoneNamingState?: "named" | "required" | null
}

export interface PublicLookupData {
  activationState: "activated" | "registration_required" | null
  assignment: AssignmentSummary | null
  compliance?: ComplianceSummary | null
  contact: ContactSummary | null
  device: DeviceSummary | null
  incidentCount: number
  kit?: PublicKitSummary | null
  medicalProfile: MedicalProfileSummary | null
  plantation?: PublicPlantationSummary | null
  physicalTag?: PublicPhysicalTagSummary | null
  publicReportingReady: boolean
  registration: RegistrationSummary | null
  scanDestination: "register" | "report" | null
  tag: string
  tagType: TagType | null
  vehicle: VehicleSummary | null
}

export interface PublicOtpStartResponse {
  debugOtp?: string
  expiresInSec: number
  resendAfterSec: number
  verificationId: string
}

export interface PublicOtpVerifyResponse {
  approved: boolean
  guardianApproval?: {
    approved: boolean
    approvedPhone?: string | null
    challengeId: string
    kitId?: string | null
    stickerId?: string | null
    tagType?: TagType | null
  }
  ownerAuth?: string
}

export interface OwnerPortalAuthToken {
  expiresAt?: string | null
  expiresInSec: number
  kitId?: string | null
  stickerId: string
  tagType: TagType | null
  token: string
  tokenType: string
}

export interface OwnerPortalOtpStartResponse {
  channel: string
  debugOtp?: string
  expiresInSec: number
  resendAfterSec: number
  verificationId: string
}

export interface OwnerPortalOtpVerifyResponse {
  approved: boolean
  ownerAuth?: OwnerPortalAuthToken | null
}

export interface OwnerPortalProfile {
  assignment: AssignmentSummary | null
  compliance?: ComplianceSummary | null
  contact: ContactSummary | null
  device: DeviceSummary | null
  incidentCount: number
  kit?: PublicKitSummary | null
  medicalProfile: MedicalProfileSummary | null
  openIncidentCount: number
  physicalTag?: PublicPhysicalTagSummary | null
  tag: string
  tagType: TagType | null
  vehicle: VehicleSummary | null
}

export interface OwnerIncidentMedia {
  aiStatus?: string | null
  aiSummary?: string | null
  createdAt?: string | null
  fileName?: string | null
  fileSize?: number | null
  id: number
  mimeType?: string | null
  sortOrder?: number | null
  uploadKey?: string | null
  url: string
}

export interface OwnerVoiceNote {
  createdAt?: string | null
  fileName?: string | null
  fileSize?: number | null
  id: number
  mimeType?: string | null
  transcript?: string | null
  transcriptLanguage?: string | null
  transcriptionStatus?: string | null
  transcribedAt?: string | null
  url: string
}

export interface OwnerIncidentUpdate {
  [key: string]: unknown
}

export interface OwnerIncidentTimelineEntry {
  [key: string]: unknown
}

export interface OwnerIncidentRecord {
  behaviorType?: string | null
  cardNumber?: string | null
  createdAt: string
  delay?: string | null
  description?: string | null
  device?: DeviceSummary | null
  dispatchedAt?: string | null
  id: number
  incidentMedia: OwnerIncidentMedia[]
  incidentStatus?: string | null
  injuryStatus?: string | null
  location?: {
    address?: string | null
    id?: number | string | null
    name?: string | null
    zone?: string | null
  } | null
  locationDescription?: string | null
  peopleInvolved?: number | string | null
  reportKind?: string | null
  resolvedAt?: string | null
  severity?: string | null
  status?: string | null
  stickerId: string
  stickerType?: TagType | null
  subjectAttribution?: {
    label?: string | null
    summary?: string | null
    type?: string | null
    vehicleContextPreserved?: boolean | null
  } | null
  subjectType?: string | null
  timeline: OwnerIncidentTimelineEntry[]
  type?: string | null
  updates: OwnerIncidentUpdate[]
  vehicle?: VehicleSummary | null
  voiceNotes: OwnerVoiceNote[]
}

export interface OwnerProfileUpdateRequest {
  allergies?: string | null
  assigneeEmail?: string | null
  assigneeName?: string | null
  assigneePhone?: string | null
  bloodType?: string | null
  companyName?: string | null
  contactEmail?: string | null
  contactName?: string | null
  contactPhone?: string | null
  email?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  fullName?: string | null
  fleetName?: string | null
  medicalConditions?: string | null
  medications?: string | null
  ownerEmail?: string | null
  ownerName?: string | null
  ownerPhone?: string | null
  phone?: string | null
  plateNumber?: string | null
  responderNotes?: string | null
  vehicleColor?: string | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vehicleType?: string | null
  vehicleYear?: number | null
  vin?: string | null
}

export interface OwnerPortalProfileUpdateEnvelope
  extends ApiEnvelope<OwnerPortalProfile> {
  reauthRequired?: boolean
}

export interface PublicUploadReference {
  capturedAt?: string | null
  durationSeconds?: number | null
  fileName: string | null
  fileSize: number | null
  fixityAlgorithm: "sha256" | null
  fixityChecksum: string | null
  languageHint?: string | null
  mimeType: string | null
  provider: "uploadthing"
  uploadKey: string | null
  uploadMetadata?: Record<string, unknown> | null
  url: string
}

export interface PublicIncidentRequest {
  actionId?: string
  behaviorType?: string
  cardNumber?: string
  delay?: string
  description?: string
  gpsAccuracy?: string
  gpsLatitude?: string
  gpsLongitude?: string
  injuryStatus?: string
  locationDescription?: string
  peopleInvolved?: string
  preferredLanguage?: "en" | "hi" | "ar"
  reportConfirmationAccepted?: boolean
  reportKind: "behavior" | "incident"
  reportPayload?: Record<string, unknown>
  reportTemplateId?: string
  reporterEmail?: string
  reporterName?: string
  reporterPhone?: string
  severity: string
  stickerId: string
  stickerType?: TagType
  tag: string
  tagType?: TagType
  type?: string
  uploadedPhotos?: PublicUploadReference[]
  uploadedVoiceNotes?: PublicUploadReference[]
}

export interface PublicIncidentResponse {
  behaviorType?: string | null
  createdAt?: string
  id?: number
  incidentStatus?: string | null
  preferredLanguage?: "en" | "hi" | "ar" | null
  reportPayload?: Record<string, unknown> | null
  reportKind?: string | null
  reportTemplateId?: string | null
  stickerId?: string | null
  stickerType?: TagType | null
  subjectType?: string | null
  type?: string | null
  vehicle?: VehicleSummary | null
}

export interface IncidentEscalationPacketPublicPayload {
  dispatch?: {
    assignments?: Array<{
      acknowledgedAt?: string | null
      agencyKind?: string | null
      dispatchReason?: string | null
      dispatchRule?: string | null
      id?: string
      jurisdictionName?: string | null
      notes?: string | null
      notifiedAt?: string | null
      organizationName?: string | null
      status?: string | null
    }>
    dispatchSummary?: {
      active?: number
      byAgencyKind?: Record<string, number>
      byStatus?: Record<string, number>
      total?: number
    }
    jurisdictionMatchStrategy?: string | null
    lastOperatorNote?: {
      createdAt?: string | null
      updateText?: string | null
      updatedBy?: string | null
    } | null
  }
  evidence?: {
    chainOfCustodyCount?: number
    photoCount?: number
    photos?: Array<{
      capturedAt?: string | null
      fileName?: string | null
      id?: number
      mimeType?: string | null
      url?: string | null
    }>
    voiceNoteCount?: number
    voiceNotes?: Array<{
      capturedAt?: string | null
      durationSeconds?: number | null
      fileName?: string | null
      id?: number
      mimeType?: string | null
      transcript?: string | null
      url?: string | null
    }>
  }
  footer?: {
    auditTimestamp?: string
    disclaimer?: string
    packetId?: string
    packetVersion?: number
  }
  header?: {
    expiresAt?: string
    generatedAt?: string
    incidentReference?: string
    incidentStatus?: string
    severity?: string
  }
  incidentSummary?: {
    description?: string | null
    injuryStatus?: string | null
    occurredAt?: string | null
    peopleInvolved?: number | null
    reportKind?: string | null
    reportedAt?: string | null
    type?: string | null
  }
  location?: {
    coordinates?: {
      accuracy?: number | null
      latitude?: number | null
      longitude?: number | null
    } | null
    jurisdictionOrOrganizationName?: string | null
    label?: string | null
    mapUrl?: string | null
    state?: string | null
  }
  packet?: {
    expiresAt?: string
    generatedAt?: string
    id?: string
    lastSharedAt?: string | null
    openCount?: number
    status?: string
    tokenPreview?: string
    url?: string
    version?: number
  }
  subjectContext?: {
    assetReference?: string | null
    organizationName?: string | null
    preferredLanguage?: string | null
    subjectLabel?: string | null
    vehiclePlate?: string | null
  }
}
export interface OperatorIncidentEscalationPacketRecord {
  currentExportId?: string | null
  expiresAt?: string | null
  generatedAt?: string | null
  lastOpenedAt?: string | null
  openCount?: number | null
  packetId?: string | null
  packetStatus?: string | null
  packetUrl?: string | null
  packetVersion?: number | null
  revoked?: boolean
  revokedAt?: string | null
  sharedAt?: string | null
  tokenPreview?: string | null
}

export interface OperatorIncidentEscalationShareResponse
  extends OperatorIncidentEscalationPacketRecord {
  attempts?: Array<{
    assignmentId?: string | null
    channel?: "email" | "sms" | "whatsapp"
    deliveryId?: string | null
    reason?: string | null
    recipient?: string | null
    recipientScopeKey?: string | null
    recipientSource?: string | null
    status?: string | null
    success?: boolean
  }>
  failureCount?: number
  shareStatus?: "failed" | "partial_success" | "skipped" | "success"
  successCount?: number
}

export interface OperatorIncidentEscalationRevokeResponse
  extends OperatorIncidentEscalationPacketRecord {
  reason?: string | null
  revoked: boolean
}

export interface PublicRegistrationRequest {
  activatedAt?: string | null
  allergies?: string | null
  assignedAt?: string | null
  assigneeEmail?: string | null
  assigneeName?: string | null
  assigneePhone?: string | null
  assignmentStatus?: string | null
  bloodType?: string | null
  companyName?: string | null
  consentAccepted?: boolean | null
  contactEmail?: string | null
  contactName?: string | null
  contactPhone?: string | null
  email?: string | null
  emergencyContactEmail?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  externalRef?: string | null
  fleetName?: string | null
  fullName?: string | null
  guardianEmail?: string | null
  guardianName?: string | null
  guardianPhone?: string | null
  guardianRelationship?: string | null
  medicalConditions?: string | null
  medications?: string | null
  memberType?: string | null
  metadata?: Record<string, unknown> | null
  noticeVersionId?: string | null
  notes?: string | null
  organizationId?: string
  ownerEmail?: string | null
  ownerName?: string | null
  ownerPhone?: string | null
  plantationId?: string | null
  plantationName?: string | null
  preferredLanguageCode?: string | null
  preferredLanguageLabel?: string | null
  phone?: string | null
  plateNumber?: string | null
  responderNotes?: string | null
  registrantRelationship?: string | null
  status?: string | null
  stickerId: string
  subjectAgeBand?: SubjectAgeBand | null
  tag: string
  tagSource?: string | null
  tagType: TagType
  type: TagType
  vehicleColor?: string | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vehicleType?: string | null
  vehicleYear?: number | null
  vin?: string | null
  zoneName?: string | null
}

export interface PublicZoneNamingRequest {
  namedBy?: string | null
  stickerId?: string
  tag: string
  zoneName: string
}

export interface PublicLookupQuery {
  gpsAccuracy?: string
  gpsLatitude?: string
  gpsLongitude?: string
  intent?: "registration"
  phone?: string
  tag: string
}

export interface ActiveNoticeQuery {
  audience?: string
  locale?: string
  surface?: string
}

export interface PrivacyRequestCreatePayload {
  intakeChannel?: string | null
  reason?: string | null
  requesterEmail?: string | null
  requesterName?: string | null
  requesterPhone?: string | null
  requestType: "access" | "correction" | "erasure" | "withdrawal"
}

export interface PublicGrievanceCreatePayload {
  caseType?: string | null
  description: string
  metadata?: Record<string, unknown> | null
  requesterEmail?: string | null
  requesterName?: string | null
  requesterPhone?: string | null
  sourceSurface?: string | null
  tag?: string | null
}

export interface FleetPilotLeadCreatePayload {
  city?: string | null
  companyName: string
  contactName: string
  currentSafetyStack?: string | null
  estimatedVehicleCount?: number | null
  fleetSegment: "trucking" | "intercity_bus" | "delivery_rider"
  fleetSizeBand?: string | null
  metadata?: Record<string, unknown> | null
  operatingRegion?: string | null
  phone: string
  pilotGoals: string
  preferredDemoWindow?: string | null
  rolloutTimeline?: string | null
  sourceSurface?: string | null
  workEmail: string
}

export interface SecurityIncidentCreatePayload {
  affectedSurface?: string | null
  certInStatus?: string | null
  closedAt?: string | null
  detectedAt?: string | null
  evidence?: Record<string, unknown> | null
  notes?: string | null
  openedAt?: string | null
  owner?: string | null
  severity: string
  sourceReference?: string | null
  sourceType: string
  statutoryNoticeStatus?: string | null
  status?: string | null
}

export interface SecurityIncidentUpdatePayload {
  affectedSurface?: string | null
  certInStatus?: string | null
  closedAt?: string | null
  detectedAt?: string | null
  evidence?: Record<string, unknown> | null
  notes?: string | null
  owner?: string | null
  severity?: string | null
  sourceReference?: string | null
  sourceType?: string | null
  statutoryNoticeStatus?: string | null
  status?: string | null
}

export interface OperatorOrganizationSummary {
  city?: string | null
  fleetCategory?: string | null
  fleetEnabled?: boolean | null
  id: string
  isActive?: boolean | null
  name: string
  region?: string | null
  slug?: string | null
  type?: string | null
}

export interface OperatorPermissions {
  authorityRole?: string | null
  dispatchIncidents?: boolean
  exportEvidence?: boolean
  manageExports?: boolean
  updateAssignments?: boolean
  updateIncidentLifecycle?: boolean
  viewAllOrganizations?: boolean
  viewAnalytics?: boolean
  [key: string]: boolean | number | string | null | undefined
}

export interface AuthorityJurisdictionSummary {
  agencyKind?: string | null
  assignmentRole?: string | null
  city?: string | null
  code?: string | null
  id: string
  isPrimary?: boolean
  level?: string | null
  name: string
  organizationId?: string | null
  region?: string | null
  stationCode?: string | null
  zone?: string | null
}

export interface OperatorAuthorityProfile {
  authorityRole?: string | null
  clerkUserId?: string | null
  department?: string | null
  email?: string | null
  fullName?: string | null
  id: string
  isOnboarded?: boolean
  isPortalAdmin?: boolean
  jurisdictions: AuthorityJurisdictionSummary[]
  organization?: OperatorOrganizationSummary | null
  organizationId?: string | null
  organizationName?: string | null
  organizationType?: string | null
  permissions?: OperatorPermissions | null
  position?: string | null
  role?: string | null
}

export interface OperatorUserProfile {
  active?: boolean
  accountStatus?: string | null
  clerkUserId?: string | null
  designation?: string | null
  email?: string | null
  firstLoginCompletedAt?: string | null
  id: string
  inviteStatus?: string | null
  isPrimaryOwner?: boolean
  name?: string | null
  organization?: OperatorOrganizationSummary | null
  phoneNumber?: string | null
  preferredLanguage?: string | null
  role?: string | null
}

export interface OperatorCurrentSession {
  actorType: "user"
  authenticated: boolean
  onboarding?: {
    completedAt?: string | null
    organizationId?: string | null
    required?: boolean
    status?: string | null
  } | null
  organization?: OperatorOrganizationSummary | null
  permissions: OperatorPermissions
  postAuthDestination: string
  user: OperatorUserProfile
}

export interface AccountOnboardingRecord {
  onboardingProfile?: OrchardOnboardingProfileRecord | null
  organization?: {
    billingEmail?: string | null
    city?: string | null
    contactEmail?: string | null
    contactPhone?: string | null
    customerCategory?: string | null
    id: string
    legalName?: string | null
    name: string
    onboardingCompletedAt?: string | null
    onboardingOwnerInternal?: string | null
    onboardingStatus?: string | null
    operatingDistrict?: string | null
    operatingPostalCode?: string | null
    operatingState?: string | null
    operatingTaluk?: string | null
    operatingVillage?: string | null
    primaryContactName?: string | null
    region?: string | null
    type?: string | null
  } | null
  user?: {
    accountStatus?: string | null
    designation?: string | null
    email?: string | null
    firstLoginCompletedAt?: string | null
    id: string
    invitationAcceptedAt?: string | null
    inviteStatus?: string | null
    isPrimaryOwner?: boolean
    name?: string | null
    phoneNumber?: string | null
    preferredLanguage?: string | null
    role?: string | null
  } | null
}

export interface AccountOnboardingUpdateRequest {
  completedStep?: string | null
  markComplete?: boolean
  onboardingProfile?: {
    approximateBlockCount?: number | null
    approximateRowCount?: number | null
    approximateTreeCount?: number | null
    defaultYieldUnit?: string | null
    goLiveTargetDate?: string | null
    implementationMode?: string | null
    orchardCount?: number | null
    preferredLanguage?: string | null
    primaryCropType?: string | null
    primaryVariety?: string | null
    qrRolloutRequired?: boolean | null
    treeImportMode?: string | null
  } | null
  organization?: {
    billingEmail?: string | null
    contactEmail?: string | null
    contactPhone?: string | null
    customerCategory?: string | null
    legalName?: string | null
    onboardingOwnerInternal?: string | null
    operatingDistrict?: string | null
    operatingPostalCode?: string | null
    operatingState?: string | null
    operatingTaluk?: string | null
    operatingVillage?: string | null
    primaryContactName?: string | null
  } | null
  user?: {
    designation?: string | null
    name?: string | null
    phoneNumber?: string | null
    preferredLanguage?: string | null
  } | null
}

export interface OperatorTimelineEntry {
  [key: string]: unknown
  action?: string | null
  eventAt?: string | null
  eventType?: string | null
  id: string
}

export interface OperatorChainOfCustodyEntry {
  [key: string]: unknown
  action?: string | null
  createdAt?: string | null
  id: string
  mediaType?: string | null
}

export interface OperatorIncidentUpdateRecord {
  createdAt?: string | null
  id: number
  updateText?: string | null
  updatedBy?: string | null
}

export interface OperatorAssignmentRecord {
  agencyKind?: string | null
  assignmentSnapshot?: Record<string, unknown> | null
  authority?: {
    fullName?: string | null
    id?: string | null
  } | null
  authorityId?: string | null
  createdAt?: string | null
  dispatchReason?: string | null
  externalReference?: string | null
  id: string
  jurisdiction?: AuthorityJurisdictionSummary | null
  notes?: string | null
  organization?: OperatorOrganizationSummary | null
  priority?: string | null
  status?: string | null
  timestamps?: {
    acknowledgedAt?: string | null
    cancelledAt?: string | null
    clearedAt?: string | null
    createdAt?: string | null
    enRouteAt?: string | null
    notifiedAt?: string | null
    onSceneAt?: string | null
    transportingAt?: string | null
  } | null
}

export interface OperatorNotificationSummary {
  authority?: {
    status?: string | null
  } | null
  emergencyContact?: {
    status?: string | null
  } | null
}

export interface OperatorExportPackage {
  checksum?: string | null
  createdAt?: string | null
  exportType?: string | null
  id: number
  purposeLabel?: string | null
  recipientKey?: string | null
  recipientType?: string | null
  sensitivityLevel?: string | null
  status?: string | null
}

export interface OperatorResponseTimesRecord {
  assignments?: Array<{
    agencyKind?: string | null
    clearanceMinutes?: number | null
    dispatchMinutes?: number | null
    id: string
    onSceneMinutes?: number | null
    status?: string | null
  }>
  clocks: {
    acknowledgementMinutes?: number | null
    closureMinutes?: number | null
    dispatchMinutes?: number | null
    enRouteMinutes?: number | null
    goldenHourDeadlineAt?: string | null
    goldenHourStatus?: string | null
    onSceneMinutes?: number | null
    resolutionMinutes?: number | null
  }
  id: number
  incidentStatus?: string | null
  severity?: string | null
  status?: string | null
  type?: string | null
}

export interface OperatorIncidentRecord {
  assignments?: OperatorAssignmentRecord[]
  behaviorType?: string | null
  cardNumber?: string | null
  chainOfCustody?: OperatorChainOfCustodyEntry[]
  contact?: ContactSummary | null
  createdAt: string
  delay?: string | null
  description?: string | null
  device?: DeviceSummary | null
  dispatchSummary?: {
    [key: string]: unknown
    active?: number | null
    total?: number | null
  } | null
  escalationPacket?: OperatorIncidentEscalationPacketRecord | null
  exportPackages?: OperatorExportPackage[]
  goldenHourDeadlineAt?: string | null
  goldenHourStatus?: string | null
  gpsAccuracy?: number | null
  gpsLatitude?: number | null
  gpsLongitude?: number | null
  id: number
  incidentLifecycle?: {
    incidentStatus?: string | null
    legacyStatus?: string | null
    timestamps?: {
      acknowledgedAt?: string | null
      activeAt?: string | null
      closedAt?: string | null
      dispatchedAt?: string | null
      enRouteAt?: string | null
      onSceneAt?: string | null
      reportedAt?: string | null
      resolvedAt?: string | null
      triagedAt?: string | null
    } | null
  } | null
  incidentMedia?: OwnerIncidentMedia[]
  incidentStatus?: string | null
  injuryStatus?: string | null
  intelligence?: {
    ai?: {
      status?: string | null
      suggestions?: {
        confidence?: number | null
        extractedData?: Record<string, unknown> | null
        keywords?: string[]
        recommendations?: unknown[]
        severity?: string | null
        type?: string | null
        urgencyScore?: number | null
      } | null
    } | null
    fraud?: {
      duplicateScore?: number | null
      score?: number | null
      signals?: Record<string, unknown> | null
      status?: string | null
    } | null
    scoring?: {
      hotspotWeight?: number | null
      safetyScoreImpact?: number | null
      scoreComponents?: Record<string, unknown> | null
    } | null
    verification?: {
      count?: number | null
      score?: number | null
      signals?: Record<string, unknown> | null
      status?: string | null
    } | null
  } | null
  jurisdiction?: (AuthorityJurisdictionSummary & {
    matchStrategy?: string | null
    snapshot?: Record<string, unknown> | null
  }) | null
  location?: {
    address?: string | null
    id?: number | string | null
    name?: string | null
    zone?: string | null
  } | null
  locationDescription?: string | null
  notificationSummary?: OperatorNotificationSummary | null
  photoUrl?: string | null
  preferredLanguage?: "en" | "hi" | "ar" | null
  reportKind?: string | null
  severity?: string | null
  status?: string | null
  stickerId?: string | null
  stickerType?: TagType | null
  subjectAttribution?: {
    label?: string | null
    summary?: string | null
    type?: string | null
  } | null
  subjectType?: string | null
  timeline?: OperatorTimelineEntry[]
  type?: string | null
  updates?: OperatorIncidentUpdateRecord[]
  vehicle?: VehicleSummary | null
  voiceNotes?: OwnerVoiceNote[]
}

export interface OperatorIncidentListQuery {
  agencyKind?: string
  behaviorType?: string
  incidentStatus?: string
  jurisdictionId?: string
  limit?: number
  locationId?: number
  offset?: number
  reportKind?: string
  severity?: string
  status?: string
}

export interface OperatorIncidentStatusUpdate {
  incidentStatus: string
  updateText?: string
  updatedBy: string
}

export interface OperatorIncidentNoteCreate {
  actionType?: string
  updateText: string
  updatedBy: string
}

export interface OperatorAssignmentUpdate {
  assignmentStatus?: string
  authorityId?: string | null
  externalReference?: string | null
  notes?: string | null
}

export interface OperatorPerformanceMetrics {
  averageClearanceTime?: number | null
  averageDispatchTime?: number | null
  averageResolutionTime?: number | null
  averageResponseTime?: number | null
  averageSceneTime?: number | null
  goldenHourComplianceRate?: number | null
  goldenHourTracked?: number | null
  goldenHourWithinTarget?: number | null
  inProgressIncidents?: number | null
  pendingIncidents?: number | null
  resolutionRate?: number | null
  resolvedIncidents?: number | null
  totalIncidents?: number | null
}

export interface OperatorBreakdownMetrics {
  byIncidentStatus?: Array<{ count: number; incidentStatus: string }>
  bySeverity?: Array<{ count: number; severity: string }>
  byType?: Array<{ count: number; type: string }>
}

export interface OperatorTimelinePoint {
  date: string
  pending: number
  resolved: number
  severe: number
  total: number
}

export interface OperatorSafetyScoreItem {
  averageImpact?: number | null
  cardNumber?: string | null
  incidentCount: number
  stickerId?: string | null
  tag: string
  totalImpact: number
}

export interface OperatorHotspotItem {
  address?: string | null
  fraudFlagged?: number | null
  locationId?: number | null
  locationName?: string | null
  moderateIncidents?: number | null
  minorIncidents?: number | null
  recent7Days?: number | null
  severeIncidents?: number | null
  totalIncidents?: number | null
  verifiedReports?: number | null
  weightedHotspotScore?: number | null
}

export interface FleetCertificationRecord {
  awardedAt?: string | null
  certificationLevel?: string | null
  createdAt?: string | null
  evidenceSnapshot?: unknown
  expiresAt?: string | null
  id: string
  incidentFreeDays?: number | null
  metadata?: unknown
  organization?: OperatorOrganizationSummary | null
  positiveActionCount?: number | null
  preventedRiskCount?: number | null
  programType?: string | null
  qualifyingIncidentCount?: number | null
  qualifyingWindow?: {
    end?: string | null
    start?: string | null
  } | null
  score?: number | null
  status?: string | null
  updatedAt?: string | null
}

export interface FleetCertificationQuery {
  includeEvidence?: boolean
  limit?: number
  organizationId?: string
  status?: string
}

export interface FleetQrOrganizationInput {
  city: string
  country?: string
  fleetAdminEmail?: string
  fleetAdminName?: string
  fleetAdminPhone?: string
  fleetCategory?: string
  fleetEnabled?: boolean
  fleetSize?: number
  name: string
  notificationEmail?: string
  region?: string
  slug?: string
  type?: string
}

export interface FleetQrRegistrationRowInput {
  activatedAt?: string
  assignmentStatus?: string
  contactEmail?: string
  contactName?: string
  contactPhone?: string
  externalRef?: string
  fleetName?: string
  memberType?: string
  notes?: string
  plateNumber?: string
  status?: string
  vehicleColor?: string
  vehicleMake?: string
  vehicleModel?: string
  vehicleType?: string
  vehicleYear?: number
}

export interface FleetQrRegisterRequest {
  defaults?: Record<string, unknown>
  dryRun?: boolean
  generateTags?: boolean
  organization?: FleetQrOrganizationInput
  organizationId?: string
  rows: FleetQrRegistrationRowInput[]
}

export interface FleetQrRegisterSummaryRow {
  data?: RegistrationSummary | null
  error?: string
  organizationId?: string | null
  rowIndex: number
  status: string
  tag?: string | null
}

export interface FleetQrRegisterResponse {
  import: {
    created: number
    dryRun: boolean
    failed: number
    rows: FleetQrRegisterSummaryRow[]
    total: number
    updated: number
  }
  organization: OperatorOrganizationRecord | null
}

export interface FleetKitIssueSlotRecord {
  label: string
  previewCode: string
  scanFlow: string
  slotKey: string
  visibility: "private" | "public"
}

export interface FleetKitIssueKitRecord {
  assetNumber: string
  assetTypeLabel: string
  operatingUnitLabel: string
  operatorLabel: string
  routeOrZone: string
  slots: FleetKitIssueSlotRecord[]
}

export interface FleetKitBatchRecord {
  activationMode: "system"
  batchCode: string
  batchLabel: string
  createdAt?: string | null
  id: string
  kits: FleetKitIssueKitRecord[]
  onboardingMode?: "import" | "manual" | null
  organizationName?: string | null
  organizationId: string
  preparedAt?: string | null
  runId?: string | null
  templateKey: string
  templateLabel: string
  totalAssets: number
  totalStickers: number
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetOnboardingPersistRequest {
  importBridge?: {
    dryRunRows?: Array<{
      plateNumber?: string | null
      rowIndex: number
      status: string
      tag?: string | null
    }>
    fileName?: string | null
    lastDryRunAt?: string | null
    sourceRows?: Array<Record<string, string>>
  } | null
  manualAssets?: Array<{
    assetNumber: string
    id: string
    notes?: string
    operatingUnitId: string
    primaryOperatorName?: string
    primaryOperatorPhone?: string
    routeOrZone?: string
    secondaryOperatorName?: string
    secondaryOperatorPhone?: string
  }>
  onboardingMode: "import" | "manual"
  operatingRegions?: string
  organization?: FleetQrOrganizationInput & {
    legalName?: string
  }
  organizationId?: string | null
  runId?: string | null
  units?: Array<{
    city?: string
    code?: string
    escalationPhone?: string
    id: string
    isPrimary?: boolean
    managerName?: string
    managerPhone?: string
    name?: string
    region?: string
    type?: string
  }>
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetOnboardingPersistResponse {
  assetCount: number
  assignmentCount: number
  onboardingMode: "import" | "manual"
  organizationId: string
  peopleCount: number
  persistedAt: string
  runId?: string | null
  unitCount: number
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetOwnerProvisioningInput {
  email: string
  name: string
  phoneNumber?: string | null
  role?: "fleet_admin" | "fleet_owner" | null
}

export interface FleetOnboardingRunRecord {
  completedAt?: string | null
  createdAt?: string | null
  currentStep: "import" | "manual" | "organization" | "overview" | "review"
  fleetOwnerUserId?: string | null
  id: string
  lastResumedAt?: string | null
  latestUpload?: {
    acceptedRows: number
    failedRows: number
    fileName?: string | null
    rowResults: Array<{
      assetNumber?: string | null
      assignmentStatus?: string | null
      errors?: string[]
      operatingUnitCode?: string | null
      rowIndex: number
      status: "accepted" | "failed"
      vehicleType?: string | null
    }>
    totalRows: number
    uploadedAt?: string | null
    validRowsCount: number
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  } | null
  latestMasterPersistence?: {
    assetCount: number
    assignmentCount: number
    mode?: "import" | "manual" | null
    organizationId?: string | null
    peopleCount: number
    persistedAt?: string | null
    runId?: string | null
    unitCount: number
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  } | null
  latestKitBatch?: {
    activationMode: "system"
    assetCount: number
    batchCode?: string | null
    batchId?: string | null
    mode?: "import" | "manual" | null
    organizationId?: string | null
    preparedAt?: string | null
    runId?: string | null
    totalStickers: number
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  } | null
  onboardingMode?: "import" | "manual" | null
  organizationId: string
  resumedByUserId?: string | null
  startedAt?: string | null
  status: "first_login_pending" | "invited" | "onboarding_complete" | "onboarding_in_progress" | "provisioned"
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  workflowStatus?: "batch_prepared" | "completed" | "onboarding_in_progress" | "review_ready"
}

export interface FleetCustomerProvisioningRequest {
  fleetOwner: FleetOwnerProvisioningInput
  organization: FleetQrOrganizationInput & {
    legalName?: string
  }
  organizationId?: string | null
  pilotLeadId?: string | null
}

export interface FleetCustomerProvisioningResponse {
  fleetOwner: OperatorUserProfile
  inviteDelivery: {
    status: "failed" | "sent" | "skipped"
  }
  onboardingRun: FleetOnboardingRunRecord | null
  organization: OperatorOrganizationRecord | null
  pilotLead?: FleetPilotLeadRecord | null
}

export interface FleetOnboardingRunResolveRequest {
  onboardingMode?: "import" | "manual" | null
  organizationId?: string | null
  runId?: string | null
  step?: "import" | "manual" | "organization" | "overview" | "review" | null
  vertical?: "intercity_bus" | "trucking" | "two_wheeler_delivery" | null
}

export interface FleetOnboardingRunResolveResponse {
  organization: OperatorOrganizationRecord | null
  run: FleetOnboardingRunRecord
}

export interface FleetKitIssueRequest {
  onboardingMode: "import" | "manual"
  operatingRegions?: string
  organization?: FleetQrOrganizationInput & {
    legalName?: string
  }
  organizationId?: string | null
  runId?: string | null
  persistedMaster?: {
    assetCount: number
    assignmentCount: number
    lastPersistedAt: string
    peopleCount: number
    runId?: string | null
    unitCount: number
  } | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetKitIssueResponse {
  batch: FleetKitBatchRecord
  summary: {
    eligibleAssets: number
    onboardingMode: "import" | "manual"
    organizationId: string
    runId: string
    skippedAssets: number
    skippedReasons: Array<{
      count: number
      reason: string
      summary: string
    }>
    totalActiveAssets: number
    totalPreparedKits: number
    totalPreparedStickers: number
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }
  organization: OperatorOrganizationRecord | null
}

export interface FleetOperatingUnitRecord {
  assetCount: number
  city: string
  code: string
  createdAt?: string | null
  escalationPhone?: string | null
  id: string
  isPrimary: boolean
  managerName?: string | null
  managerPhone?: string | null
  metadata?: Record<string, unknown> | null
  name: string
  organizationId: string
  peopleCount: number
  region?: string | null
  type: string
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetAssetRecord {
  assetNumber: string
  assetType: string
  assignmentCount: number
  createdAt?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  notes?: string | null
  operatingUnit?: {
    city?: string | null
    code: string
    id: string
    name: string
  } | null
  operatingUnitId?: string | null
  organizationId: string
  routeOrZone?: string | null
  sourceMode?: string | null
  status: string
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetPersonRecord {
  assignmentCount: number
  createdAt?: string | null
  fullName: string
  id: string
  metadata?: Record<string, unknown> | null
  operatingUnit?: {
    city?: string | null
    code: string
    id: string
    name: string
  } | null
  operatingUnitId?: string | null
  organizationId: string
  phone?: string | null
  primaryAssignmentCount: number
  roleType: string
  secondaryAssignmentCount: number
  sourceMode?: string | null
  status: string
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetAssignmentRecord {
  asset?: {
    assetNumber: string
    assetType: string
    id: string
    operatingUnit?: {
      code: string
      id: string
      name: string
    } | null
    routeOrZone?: string | null
    status: string
  } | null
  assetId: string
  assignmentStatus: string
  createdAt?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  organizationId: string
  primaryPerson?: {
    fullName: string
    id: string
    phone?: string | null
    roleType: string
    status: string
  } | null
  primaryPersonId?: string | null
  secondaryPerson?: {
    fullName: string
    id: string
    phone?: string | null
    roleType: string
    status: string
  } | null
  secondaryPersonId?: string | null
  sourceMode?: string | null
  updatedAt?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetOperatingUnitUpsertRequest {
  city: string
  code: string
  escalationPhone?: string | null
  isPrimary?: boolean
  managerName?: string | null
  managerPhone?: string | null
  name: string
  organizationId: string
  region?: string | null
  type?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetAssetUpsertRequest {
  assetNumber: string
  assetType: string
  notes?: string | null
  operatingUnitId?: string | null
  organizationId: string
  routeOrZone?: string | null
  status?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetPersonUpsertRequest {
  fullName: string
  operatingUnitId?: string | null
  organizationId: string
  phone?: string | null
  roleType: string
  status?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetAssignmentUpsertRequest {
  assetId: string
  assignmentStatus?: string | null
  organizationId: string
  primaryPersonId?: string | null
  secondaryPersonId?: string | null
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetCsvImportSummaryRow {
  data?: RegistrationSummary | null
  error?: string
  input?: Record<string, unknown> | null
  organizationId?: string | null
  rowIndex: number
  status: string
  tag?: string | null
}

export interface FleetCsvImportSummary {
  created: number
  dryRun: boolean
  failed: number
  rows: FleetCsvImportSummaryRow[]
  total: number
  updated: number
}

export interface FleetOnboardingUploadRequest {
  fileName?: string | null
  organization?: {
    city?: string | null
    country?: string | null
    fleetAdminEmail?: string | null
    fleetAdminName?: string | null
    fleetAdminPhone?: string | null
    legalName?: string | null
    name?: string | null
    region?: string | null
    type?: string | null
  } | null
  organizationId?: string | null
  runId?: string | null
  rows: Array<Record<string, string>>
  units?: Array<{
    city?: string
    code?: string
    id: string
    isPrimary?: boolean
    name?: string
  }>
  vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
}

export interface FleetOnboardingUploadResponse {
  acceptedRows: number
  contract: {
    allowedAssignmentStatuses: string[]
    expectedVehicleType: string
    requiredColumns: {
      assetIdentifier: string[]
      operatingUnitCode: string[]
    }
  }
  failedRows: number
  rowResults: Array<{
    assetNumber?: string | null
    assignmentStatus?: string | null
    errors: string[]
    operatingUnitCode?: string | null
    rowIndex: number
    status: "accepted" | "failed"
    vehicleType?: string | null
  }>
  runId: string
  totalRows: number
  uploadedAt: string
  validRows: Array<{
    assetNumber: string
    assignmentStatus: string
    notes?: string | null
    operatingUnitCode: string
    primaryOperatorName: string
    primaryOperatorPhone?: string | null
    routeOrZone?: string | null
    secondaryOperatorName?: string | null
    secondaryOperatorPhone?: string | null
    vehicleType: string
  }>
}

export interface OperatorCardRecord {
  cardNumber: string
  createdAt?: string | null
  deployedAt?: string | null
  location?: {
    address?: string | null
    id?: number | string | null
    name?: string | null
  } | null
  locationId?: number | null
  organization?: OperatorOrganizationSummary | null
  organizationId?: string | null
  updatedAt?: string | null
}

export interface OperatorOrganizationRecord extends OperatorOrganizationSummary {
  address?: string | null
  authorityCount?: number
  billingEmail?: string | null
  cardCount?: number
  contactEmail?: string | null
  contactPhone?: string | null
  country?: string | null
  customerCategory?: string | null
  fleetAdminEmail?: string | null
  fleetAdminName?: string | null
  fleetAdminPhone?: string | null
  fleetMetadata?: Record<string, unknown> | null
  fleetSize?: number | null
  legalName?: string | null
  locationCount?: number
  notificationEmail?: string | null
  notificationPhone?: string | null
  notificationWhatsapp?: string | null
  onboardingCompletedAt?: string | null
  onboardingOwnerInternal?: string | null
  onboardingStatus?: string | null
  operatingDistrict?: string | null
  operatingPostalCode?: string | null
  operatingState?: string | null
  operatingTaluk?: string | null
  operatingVillage?: string | null
  pilotStartedAt?: string | null
  pilotStatus?: string | null
  primaryContactName?: string | null
  userCount?: number
}

export interface OrchardOnboardingProfileRecord {
  approximateBlockCount?: number | null
  approximateRowCount?: number | null
  approximateTreeCount?: number | null
  completedAt?: string | null
  completedStep?: string | null
  createdAt?: string | null
  defaultYieldUnit?: string | null
  goLiveTargetDate?: string | null
  id: string
  implementationMode?: string | null
  orchardCount?: number | null
  organizationId: string
  preferredLanguage?: string | null
  primaryCropType?: string | null
  primaryOwnerUserId?: string | null
  primaryVariety?: string | null
  qrRolloutRequired?: boolean | null
  setupStatus?: string | null
  trackFlowering?: boolean | null
  trackFruitLoad?: boolean | null
  trackFruitSet?: boolean | null
  trackHarvest?: boolean | null
  trackHealthCheck?: boolean | null
  treeImportMode?: string | null
  updatedAt?: string | null
}

export interface OperatorOrganizationDetail extends OperatorOrganizationRecord {
  authorities?: Array<{
    email?: string | null
    fullName?: string | null
    id: string
    position?: string | null
  }>
  locations?: Array<{
    address?: string | null
    id: number | string
    name: string
  }>
  nfcCards?: OperatorCardRecord[]
  orchardOnboardingProfile?: OrchardOnboardingProfileRecord | null
  users?: Array<{
    accountStatus?: string | null
    designation?: string | null
    email?: string | null
    id: string
    inviteStatus?: string | null
    isPrimaryOwner?: boolean | null
    name?: string | null
    phoneNumber?: string | null
    preferredLanguage?: string | null
    role?: string | null
  }>
  _count?: {
    authorities?: number
    fleetTags?: number
    incidents?: number
    locations?: number
    nfcCards?: number
    personalTags?: number
    users?: number
    vehicleStickers?: number
  } | null
}

export interface OrchardRecord {
  areaAcres?: number | null
  areaHectares?: number | null
  boundaryGeoJson?: Record<string, unknown> | null
  createdAt?: string | null
  cropType: string
  district?: string | null
  id: string
  latitude?: number | null
  longitude?: number | null
  managerName?: string | null
  managerPhone?: string | null
  metadata?: Record<string, unknown> | null
  name: string
  organizationId?: string | null
  ownerName?: string | null
  plantingYear?: number | null
  primaryVariety?: string | null
  state?: string | null
  taluk?: string | null
  updatedAt?: string | null
  village?: string | null
  _count?: {
    blocks?: number
    observations?: number
    trees?: number
  } | null
}

export interface OrchardBlockRecord {
  code: string
  createdAt?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  name: string
  orchardId: string
  organizationId?: string | null
  rows?: OrchardRowRecord[]
  sequence: number
  updatedAt?: string | null
  varietyOverride?: string | null
  _count?: {
    observations?: number
    rows?: number
    trees?: number
  } | null
}

export interface OrchardRowRecord {
  blockId: string
  code: string
  createdAt?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  orchardId: string
  organizationId?: string | null
  orientation?: string | null
  sequence: number
  updatedAt?: string | null
}

export interface OrchardObservationPhotoRecord {
  createdAt?: string | null
  fileName?: string | null
  fileSize?: number | null
  id: string
  metadata?: Record<string, unknown> | null
  mimeType?: string | null
  observationId: string
  provider?: string | null
  uploadKey?: string | null
  url: string
}

export interface OrchardObservationPhotoInput {
  fileName?: string | null
  fileSize?: number | null
  metadata?: Record<string, unknown> | null
  mimeType?: string | null
  provider?: string | null
  uploadKey?: string | null
  url: string
}

export interface OrchardObservationRecord {
  blockId: string
  createdAt?: string | null
  diseaseStatus?: string | null
  estimatedFruitLoad?: string | null
  floweringIntensity?: string | null
  floweringPresent?: boolean | null
  fruitSetPresent?: boolean | null
  fruitSetQuality?: string | null
  harvestRound?: number | null
  healthStatus?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  notes?: string | null
  observationType: string
  observedAt?: string | null
  observedById?: string | null
  observedByName?: string | null
  orchardId: string
  organizationId?: string | null
  pestStatus?: string | null
  photos?: OrchardObservationPhotoRecord[]
  rowId?: string | null
  severity?: string | null
  treeId: string
  updatedAt?: string | null
  yieldQuantity?: number | null
  yieldUnit?: string | null
}

export interface OrchardAnalyticsObservationTypeCounts {
  flowering: number
  fruit_load: number
  fruit_set: number
  harvest: number
  health_check: number
  other: number
}

export interface OrchardAnalyticsMonthlyActivityPoint {
  monthKey: string
  monthLabel: string
  observationCount: number
}

export interface OrchardAnalyticsHarvestYieldPoint {
  entryCount: number
  totalQuantity: number
  unit: string
}

export interface OrchardAnalyticsTreeFlagRecord {
  averageHarvestQuantityPerEntry?: number | null
  blockCode?: string | null
  blockId: string
  harvestEntryCount?: number | null
  orchardBaselineQuantity?: number | null
  primaryHarvestUnit?: string | null
  rowCode?: string | null
  treeCode: string
  treeId: string
}

export interface OrchardAnalyticsBlockActivityPoint {
  blockCode?: string | null
  blockId: string
  blockName?: string | null
  observationCount: number
}

export interface OrchardAnalyticsBlockCoverageFlagRecord {
  blockCode?: string | null
  blockId: string
  blockName?: string | null
  coverageRatio: number
  observedTreeCount: number
  totalTreeCount: number
}

export interface OrchardAnalyticsBlockAnomalyRecord {
  anomalyReasons: string[]
  averageHarvestQuantityPerEntry?: number | null
  blockCode?: string | null
  blockId: string
  blockName?: string | null
  coverageRatio: number
  observedTreeCount: number
  observationCount: number
  primaryHarvestUnit?: string | null
  totalHarvestQuantity?: number | null
  totalTreeCount: number
}

export interface OrchardAnalyticsSeasonalComparisonPeriod {
  endAt?: string | null
  floweringEntries: number
  fruitLoadEntries: number
  fruitSetEntries: number
  harvestEntries: number
  healthCheckEntries: number
  latestHarvestAt?: string | null
  latestHarvestRound?: number | null
  observedTreeCount: number
  startAt?: string | null
  totalObservations: number
}

export interface OrchardAnalyticsSeasonalComparisonDelta {
  floweringEntries: number
  fruitLoadEntries: number
  fruitSetEntries: number
  harvestEntries: number
  healthCheckEntries: number
  observedTreeCount: number
  totalObservations: number
}

export interface OrchardAnalyticsSeasonalComparison {
  current: OrchardAnalyticsSeasonalComparisonPeriod
  delta: OrchardAnalyticsSeasonalComparisonDelta
  previous: OrchardAnalyticsSeasonalComparisonPeriod
}

export interface OrchardAnalyticsPerformanceFlags {
  blockAnomalies: OrchardAnalyticsBlockAnomalyRecord[]
  blockAnomaliesCount: number
  blocksWithLowObservationCoverage: OrchardAnalyticsBlockCoverageFlagRecord[]
  blocksWithLowObservationCoverageCount: number
  inactiveButStillScanned: OrchardAnalyticsTreeFlagRecord[]
  inactiveButStillScannedCount: number
  lowPerformingTrees: OrchardAnalyticsTreeFlagRecord[]
  lowPerformingTreesCount: number
  treesWithNoRecentHarvest: OrchardAnalyticsTreeFlagRecord[]
  treesWithNoRecentHarvestCount: number
}

export interface OrchardAnalyticsSeasonalSummary {
  floweringEntries: number
  fruitLoadEntries: number
  fruitSetEntries: number
  harvestEntries: number
  healthCheckEntries: number
  latestHarvestAt?: string | null
  latestHarvestRound?: number | null
}

export interface OrchardAnalyticsRecord {
  activeTreeCount: number
  blockActivity: OrchardAnalyticsBlockActivityPoint[]
  dateFrom?: string | null
  dateTo?: string | null
  harvestYieldByUnit: OrchardAnalyticsHarvestYieldPoint[]
  latestObservationAt?: string | null
  managerSummary: string[]
  monthlyActivity: OrchardAnalyticsMonthlyActivityPoint[]
  observationTypeCounts: OrchardAnalyticsObservationTypeCounts
  observedTreeCount: number
  orchardId: string
  pendingObservationTreeCount: number
  performanceFlags: OrchardAnalyticsPerformanceFlags
  seasonalComparison: OrchardAnalyticsSeasonalComparison
  seasonalSummary: OrchardAnalyticsSeasonalSummary
  totalObservations: number
  treeCount: number
}

export interface OrchardPortfolioAnalyticsOrchardRecord {
  activeTreeCount: number
  ageBandKey?: string | null
  ageBandLabel?: string | null
  averageHarvestQuantityPerEntry?: number | null
  averageTreeAgeYears?: number | null
  dominantVariety?: string | null
  harvestEntries: number
  latestObservationAt?: string | null
  observationCoverageRatio: number
  observedTreeCount: number
  orchardId: string
  orchardName: string
  pendingObservationTreeCount: number
  primaryHarvestUnit?: string | null
  primaryVariety?: string | null
  totalObservations: number
  totalHarvestQuantity?: number | null
  treeCount: number
  yieldPatternKey?: string | null
  yieldPatternLabel?: string | null
}

export interface OrchardPortfolioBenchmarkGroup {
  averageHarvestQuantityPerEntry?: number | null
  averageObservationCoverageRatio: number
  groupKey: string
  groupLabel: string
  orchardCount: number
  primaryHarvestUnit?: string | null
  topOrchards: OrchardPortfolioAnalyticsOrchardRecord[]
}

export interface OrchardPortfolioAnalyticsRecord {
  ageBandBenchmarks: OrchardPortfolioBenchmarkGroup[]
  activeTreeCount: number
  dateFrom?: string | null
  dateTo?: string | null
  lowCoverageOrchards: OrchardPortfolioAnalyticsOrchardRecord[]
  monthlyActivity: OrchardAnalyticsMonthlyActivityPoint[]
  mostActiveOrchards: OrchardPortfolioAnalyticsOrchardRecord[]
  observationTypeCounts: OrchardAnalyticsObservationTypeCounts
  observedTreeCount: number
  orchardComparisons: OrchardPortfolioAnalyticsOrchardRecord[]
  orchardCount: number
  orchardsWithActivityCount: number
  totalObservations: number
  treeCount: number
  varietyBenchmarks: OrchardPortfolioBenchmarkGroup[]
  yieldPatternBenchmarks: OrchardPortfolioBenchmarkGroup[]
}

export interface OrchardTreeRecord {
  ageYears?: number | null
  block?: {
    code?: string | null
    id?: string | null
    name?: string | null
  } | null
  blockId: string
  createdAt?: string | null
  id: string
  latitude?: number | null
  latestObservation?: OrchardObservationRecord | null
  longitude?: number | null
  metadata?: Record<string, unknown> | null
  orchard?: {
    cropType?: string | null
    id?: string | null
    name?: string | null
  } | null
  orchardId: string
  organizationId?: string | null
  plantingDate?: string | null
  plantingYear?: number | null
  qrToken: string
  replacementOfTreeId?: string | null
  row?: {
    code?: string | null
    id?: string | null
  } | null
  rowId?: string | null
  status: string
  treeCode: string
  updatedAt?: string | null
  variety?: string | null
}

export interface OrchardDetailRecord extends OrchardRecord {
  blocks?: OrchardBlockRecord[]
}

export interface OrchardCreateRequest {
  areaAcres?: number | null
  areaHectares?: number | null
  boundaryGeoJson?: Record<string, unknown> | null
  cropType: string
  district?: string | null
  latitude?: number | null
  longitude?: number | null
  managerName?: string | null
  managerPhone?: string | null
  metadata?: Record<string, unknown> | null
  name: string
  organizationId?: string | null
  ownerName?: string | null
  plantingYear?: number | null
  primaryVariety?: string | null
  state?: string | null
  taluk?: string | null
  village?: string | null
}

export type OrchardUpdateRequest = OrchardCreateRequest

export interface OrchardBlockCreateRequest {
  code: string
  metadata?: Record<string, unknown> | null
  name: string
  sequence?: number | null
  varietyOverride?: string | null
}

export interface OrchardRowCreateRequest {
  blockId: string
  code: string
  metadata?: Record<string, unknown> | null
  orientation?: string | null
  sequence?: number | null
}

export interface OrchardTreeCreateRequest {
  ageYears?: number | null
  blockId: string
  latitude?: number | null
  longitude?: number | null
  metadata?: Record<string, unknown> | null
  plantingDate?: string | null
  plantingYear?: number | null
  qrToken: string
  replacementOfTreeId?: string | null
  rowId?: string | null
  status?: string | null
  treeCode: string
  variety?: string | null
}

export interface OrchardObservationCreateRequest {
  diseaseStatus?: string | null
  estimatedFruitLoad?: string | null
  floweringIntensity?: string | null
  floweringPresent?: boolean | null
  fruitSetPresent?: boolean | null
  fruitSetQuality?: string | null
  harvestRound?: number | null
  healthStatus?: string | null
  metadata?: Record<string, unknown> | null
  notes?: string | null
  observationType: string
  observedAt?: string | null
  observedById?: string | null
  observedByName?: string | null
  pestStatus?: string | null
  photos?: OrchardObservationPhotoInput[]
  qrToken: string
  severity?: string | null
  yieldQuantity?: number | null
  yieldUnit?: string | null
}

export interface OrchardTreeImportErrorRecord {
  message: string
  row?: Record<string, unknown> | null
  rowNumber: number
}

export interface OrchardTreeImportSummary {
  created: number
  dryRun?: boolean
  errors: OrchardTreeImportErrorRecord[]
  failed: number
  totalRows: number
}

export interface PlantationPolicyRecord {
  configStatus?: "configured" | "defaulted"
  defaultLocalLanguageCode?: string | null
  dualLanguageEnabled: boolean
  notifyEmergencyContactsOnPanic: boolean
  notifyEmergencyContactsOnReport: boolean
  notifyNearestAuthority: boolean
  organizationId?: string | null
  panicSmsEnabled: boolean
  panicSmsRecipientLimit: number
  plantationId: string
  plantationName?: string | null
  sirenCooldownMinutes: number
  sirenFailureDisableThreshold: number
  sirenHeartbeatStaleMinutes: number
  sirenRoutingMode?:
    | "all_site_sirens"
    | "zone_only"
    | "zone_plus_central"
    | "healthy_zone_then_central"
    | "ordered_health_escalation"
    | "zone_then_nearest_healthy"
    | "nearest_healthy_then_central"
    | null
  triggerSirensOnPanic: boolean
  version?: string | null
}

export interface PlantationPolicyUpdateRequest {
  defaultLocalLanguageCode?: string | null
  dualLanguageEnabled: boolean
  notifyEmergencyContactsOnPanic: boolean
  notifyEmergencyContactsOnReport: boolean
  notifyNearestAuthority: boolean
  panicSmsEnabled: boolean
  panicSmsRecipientLimit: number
  plantationName?: string | null
  sirenCooldownMinutes: number
  sirenFailureDisableThreshold: number
  sirenHeartbeatStaleMinutes: number
  sirenRoutingMode?:
    | "all_site_sirens"
    | "zone_only"
    | "zone_plus_central"
    | "healthy_zone_then_central"
    | "ordered_health_escalation"
    | "zone_then_nearest_healthy"
    | "nearest_healthy_then_central"
    | null
  triggerSirensOnPanic: boolean
}

export interface PlantationSirenMaintenanceEventRecord {
  actorId?: string | null
  actorName?: string | null
  createdAt?: string | null
  eventType: string
  id: string
  metadata?: Record<string, unknown> | null
  note?: string | null
  scheduledEndAt?: string | null
  scheduledStartAt?: string | null
  sourceType?: string | null
}

export interface PlantationSirenHeartbeatProvisioning {
  apiKey: string
  endpointPath: string
  headerName: string
  preview: string
  sirenId?: string | null
}

export interface PlantationSirenRecord {
  consecutiveFailureCount?: number
  createdAt?: string | null
  displayName: string
  escalationOrder: number
  failureDisabledAt?: string | null
  failureDisabledReason?: string | null
  healthState?: string | null
  heartbeatKeyPreview?: string | null
  heartbeatProvisionedAt?: string | null
  heartbeatProvisionedById?: string | null
  heartbeatProvisionedByName?: string | null
  heartbeatProvisioning?: PlantationSirenHeartbeatProvisioning | null
  id: string
  isActive: boolean
  firstDeviceCheckInAt?: string | null
  lastHeartbeatPayload?: Record<string, unknown> | null
  lastHeartbeatSource?: string | null
  lastFailureAt?: string | null
  lastHeartbeatAt?: string | null
  lastSeenAt?: string | null
  latitude?: number | null
  locationLabel?: string | null
  longitude?: number | null
  maintenanceMode?: boolean
  maintenanceHistory?: PlantationSirenMaintenanceEventRecord[]
  maintenanceReason?: string | null
  metadata?: Record<string, unknown> | null
  organizationId?: string | null
  phoneNumber?: string | null
  plantationId: string
  plantationName?: string | null
  provisioningStatus?: string | null
  scheduledMaintenanceEndAt?: string | null
  scheduledMaintenanceNote?: string | null
  scheduledMaintenanceStartAt?: string | null
  triggerOnPanic: boolean
  updatedAt?: string | null
  zoneName?: string | null
}

export interface PlantationSirenUpsertRequest {
  displayName?: string | null
  escalationOrder?: number | null
  isActive?: boolean
  latitude?: number | null
  locationLabel?: string | null
  longitude?: number | null
  maintenanceMode?: boolean
  maintenanceReason?: string | null
  metadata?: Record<string, unknown> | null
  organizationId?: string | null
  phoneNumber: string
  plantationName?: string | null
  scheduledMaintenanceEndAt?: string | null
  scheduledMaintenanceNote?: string | null
  scheduledMaintenanceStartAt?: string | null
  triggerOnPanic?: boolean
  zoneName?: string | null
}

export interface OperatorOrganizationCreateRequest {
  address?: string | null
  approximateBlockCount?: number | null
  approximateRowCount?: number | null
  approximateTreeCount?: number | null
  billingEmail?: string | null
  city: string
  contactEmail?: string | null
  contactPhone?: string | null
  country?: string | null
  customerCategory?: string | null
  defaultYieldUnit?: string | null
  fleetAdminEmail?: string | null
  fleetAdminName?: string | null
  fleetAdminPhone?: string | null
  fleetCategory?: string | null
  fleetEnabled?: boolean | null
  fleetMetadata?: Record<string, unknown> | null
  fleetSize?: number | null
  goLiveTargetDate?: string | null
  implementationMode?: string | null
  legalName?: string | null
  name: string
  notificationEmail?: string | null
  notificationPhone?: string | null
  notificationWhatsapp?: string | null
  onboardingOwnerInternal?: string | null
  operatingDistrict?: string | null
  operatingPostalCode?: string | null
  operatingState?: string | null
  operatingTaluk?: string | null
  operatingVillage?: string | null
  orchardCount?: number | null
  pilotStartedAt?: string | null
  pilotStatus?: string | null
  primaryContactName?: string | null
  primaryCropType?: string | null
  primaryOwnerDesignation?: string | null
  primaryOwnerEmail?: string | null
  primaryOwnerName?: string | null
  primaryOwnerPhone?: string | null
  primaryOwnerPreferredLanguage?: string | null
  primaryVariety?: string | null
  qrRolloutRequired?: boolean | null
  region?: string | null
  slug?: string | null
  treeImportMode?: string | null
  type: string
}

export interface OperatorOrganizationUpdateRequest
  extends Partial<OperatorOrganizationCreateRequest> {
  isActive?: boolean
}

export type B2cStockTagType = "vehicle_sticker" | "personal_tag"
export type B2cStockKitVariant = SafetyQrKitVariant
export type B2cStockLifecycleState = "generated" | "printed" | "distributed"

export interface B2cStockBatchRecord {
  batchCode: string
  createdAt?: string | null
  distributedAt?: string | null
  firstKitCode?: string | null
  generatedById?: string | null
  generatedByName?: string | null
  id: string
  kitCount?: number
  kitVariant?: B2cStockKitVariant | null
  lastKitCode?: string | null
  lifecycleState: B2cStockLifecycleState
  notes?: string | null
  organization?: OperatorOrganizationSummary | null
  organizationId?: string | null
  printedAt?: string | null
  productFamily?: SafetyQrProductFamily | null
  publicBaseUrl?: string | null
  quantity: number
  quantityKits?: number | null
  tagCount?: number
  tagType?: B2cStockTagType | null
  updatedAt?: string | null
}

export interface B2cStockPhysicalTagRecord {
  createdAt?: string | null
  iconVariant?: string | null
  id?: string | null
  identifierDisplayType?: string | null
  identifierLabel?: string | null
  kitCode?: string | null
  kitId?: string | null
  placement?: string | null
  postActivationRoutePolicy?: string | null
  preActivationRoutePolicy?: string | null
  primaryCopy?: string | null
  printSlot?: string | null
  printTemplateId?: string | null
  qrTargetUrl?: string | null
  reportTemplateId?: string | null
  role?: string | null
  scanDestinationHint?: string | null
  sequenceNumber: number
  sizeMm?: {
    height?: number
    width?: number
  } | null
  status?: string | null
  tag: string
  tagSlot?: string | null
  updatedAt?: string | null
}

export interface B2cStockProductKitRecord {
  activationState?: string | null
  createdAt?: string | null
  id: string
  kitCode: string
  kitVariant?: B2cStockKitVariant | null
  lifecycleState?: B2cStockLifecycleState | string | null
  solutionCategory?: SafetyQrSolutionCategory
  physicalTags: B2cStockPhysicalTagRecord[]
  printTemplateId?: string | null
  productFamily?: SafetyQrProductFamily | null
  quantityTags?: number
  sequenceNumber: number
  updatedAt?: string | null
  vehicleClass?: SafetyQrVehicleClass
}

export interface B2cStockBatchDetail extends B2cStockBatchRecord {
  items: B2cStockPhysicalTagRecord[]
  kits: B2cStockProductKitRecord[]
}

export interface B2cStockBatchQuery {
  kitVariant?: B2cStockKitVariant
  lifecycleState?: B2cStockLifecycleState
  limit?: number
  organizationId?: string
}

export interface B2cStockBatchCreateRequest {
  kitVariant: B2cStockKitVariant
  notes?: string | null
  organizationId?: string | null
  quantityKits: number
}

export interface B2cStockBatchDeleteOtpStartRequest {
  batchIds: string[]
}

export interface B2cStockBatchDeleteOtpStartResponse {
  batchCodes: string[]
  batchCount: number
  channel?: string | null
  expiresInSec?: number | null
  maskedPhone?: string | null
  resendAfterSec?: number | null
  verificationId: string
}

export interface B2cStockBatchDeleteSelectedRequest {
  batchIds: string[]
  otp: string
  verificationId: string
}

export interface B2cStockBatchDeleteSelectedResponse {
  batchCodes: string[]
  batchCount: number
  batchIds: string[]
  deletedBatches: number
  deletedKitRegistrations: number
  deletedLegacyPersonalTags: number
  deletedLegacyVehicleStickers: number
  deletedPhysicalTags: number
  deletedProductKits: number
  deletedRegistrationCompliances: number
}

export interface OpsMonitoringScope {
  actorType?: string | null
  organizationId?: string | null
  organizationName?: string | null
  role?: string | null
  visibility?: "global" | "organization" | null
}

export interface OpsMonitoringNotificationIssue {
  attemptCount?: number | null
  channel?: string | null
  detailedStatus?: string | null
  detailedStatusCode?: string | null
  errorMessage?: string | null
  failedAt?: string | null
  id: string
  incidentId?: number | null
  maxAttempts?: number | null
  nextAttemptAt?: string | null
  organizationId?: string | null
  organizationName?: string | null
  recipient?: string | null
  status?: string | null
  updatedAt?: string | null
}

export interface OpsMonitoringPostProcessIssue {
  ageMinutes?: number | null
  attentionLabel?: string | null
  attentionReason?: string | null
  attentionSeverity?: string | null
  id: string
  incidentId?: number | null
  maxRetries?: number | null
  nextRetryAt?: string | null
  organizationId?: string | null
  organizationName?: string | null
  requestedAt?: string | null
  retryCount?: number | null
  stage?: string | null
  status?: string | null
}

export interface OpsMonitoringRoutingGap {
  activeAuthorityCount?: number | null
  agencyKind?: string | null
  authorityCount?: number | null
  city?: string | null
  id: string
  issueLabel?: string | null
  issueSeverity?: string | null
  issueSummary?: string | null
  jurisdictionName?: string | null
  missingChannels?: string[]
  organizationId?: string | null
  organizationName?: string | null
  reachableChannels?: string[]
  region?: string | null
}

export interface OpsMonitoringGrievanceCase {
  assignee?: string | null
  caseType?: string | null
  createdAt?: string | null
  id: string
  isOverdue?: boolean | null
  organizationId?: string | null
  organizationName?: string | null
  registrationComplianceId?: string | null
  requesterName?: string | null
  slaDueAt?: string | null
  sourceSurface?: string | null
  status?: string | null
  tag?: string | null
}

export interface OpsMonitoringFleetPilotLead {
  city?: string | null
  companyName?: string | null
  contactName?: string | null
  createdAt?: string | null
  currentSafetyStack?: string | null
  estimatedVehicleCount?: number | null
  fleetSegment?: string | null
  fleetSizeBand?: string | null
  id: string
  notificationError?: string | null
  notificationStatus?: string | null
  notifiedAt?: string | null
  operatingRegion?: string | null
  organizationId?: string | null
  organizationName?: string | null
  phone?: string | null
  pilotGoals?: string | null
  preferredDemoWindow?: string | null
  rolloutTimeline?: string | null
  sourceSurface?: string | null
  status?: string | null
  updatedAt?: string | null
  workEmail?: string | null
}

export interface OpsMonitoringSirenActivation {
  acknowledgedAt?: string | null
  acknowledgedByName?: string | null
  attemptedAt?: string | null
  channel?: string | null
  consecutiveFailureCount?: number | null
  createdAt?: string | null
  displayName?: string | null
  errorMessage?: string | null
  id: string
  incidentId?: number | null
  lastHeartbeatAt?: string | null
  lastHeartbeatSource?: string | null
  lastSeenAt?: string | null
  organizationId?: string | null
  organizationName?: string | null
  operatorStatus?: string | null
  plantationId?: string | null
  plantationName?: string | null
  providerMessageId?: string | null
  recipientPhone?: string | null
  routeScope?: string | null
  sirenHealthState?: string | null
  status?: string | null
  success?: boolean | null
  deliveredAt?: string | null
  lastRetriedAt?: string | null
  lastRetriedByName?: string | null
  maintenanceMode?: boolean | null
  manualRetryCount?: number | null
  scheduledMaintenanceEndAt?: string | null
  scheduledMaintenanceStartAt?: string | null
  zoneName?: string | null
}

export interface OpsMonitoringSirenMaintenanceEvent {
  actorId?: string | null
  actorName?: string | null
  createdAt?: string | null
  eventType?: string | null
  id: string
  metadata?: Record<string, unknown> | null
  note?: string | null
  scheduledEndAt?: string | null
  scheduledStartAt?: string | null
  sourceType?: string | null
}

export interface OpsMonitoringSirenRegistryRecord {
  consecutiveFailureCount?: number | null
  displayName?: string | null
  escalationOrder?: number | null
  failureDisabledAt?: string | null
  failureDisabledReason?: string | null
  firstDeviceCheckInAt?: string | null
  heartbeatProvisionedAt?: string | null
  heartbeatProvisionedById?: string | null
  heartbeatProvisionedByName?: string | null
  healthState?: string | null
  id: string
  isActive?: boolean | null
  lastHeartbeatAt?: string | null
  lastHeartbeatSource?: string | null
  lastSeenAt?: string | null
  latitude?: number | null
  locationLabel?: string | null
  longitude?: number | null
  maintenanceHistory?: OpsMonitoringSirenMaintenanceEvent[]
  maintenanceMode?: boolean | null
  maintenanceReason?: string | null
  onboardingState?: string | null
  organizationId?: string | null
  organizationName?: string | null
  plantationId?: string | null
  plantationName?: string | null
  provisioningStatus?: string | null
  scheduledMaintenanceEndAt?: string | null
  scheduledMaintenanceNote?: string | null
  scheduledMaintenanceStartAt?: string | null
  triggerOnPanic?: boolean | null
  zoneName?: string | null
}

export interface OpsMonitoringSirenOnboardingRecord {
  displayName?: string | null
  firstDeviceCheckInAt?: string | null
  heartbeatProvisionedAt?: string | null
  heartbeatProvisionedById?: string | null
  heartbeatProvisionedByName?: string | null
  healthState?: string | null
  id: string
  locationLabel?: string | null
  maintenanceHistory?: OpsMonitoringSirenMaintenanceEvent[]
  minutesSinceFirstCheckIn?: number | null
  minutesSinceProvisioned?: number | null
  onboardingState?: string | null
  organizationId?: string | null
  organizationName?: string | null
  plantationId?: string | null
  plantationName?: string | null
  provisioningStatus?: string | null
  zoneName?: string | null
}

export interface OpsSirenRoutingPreviewItem {
  consecutiveFailureCount?: number | null
  displayName?: string | null
  distanceMeters?: number | null
  escalationOrder?: number | null
  failureDisabledAt?: string | null
  failureDisabledReason?: string | null
  healthAvailable?: boolean | null
  healthState?: string | null
  id: string
  isActive?: boolean | null
  isCentral?: boolean | null
  isZoneMatch?: boolean | null
  lastHeartbeatAt?: string | null
  lastHeartbeatSource?: string | null
  lastSeenAt?: string | null
  latitude?: number | null
  locationLabel?: string | null
  longitude?: number | null
  maintenanceMode?: boolean | null
  scheduledMaintenanceEndAt?: string | null
  scheduledMaintenanceNote?: string | null
  scheduledMaintenanceStartAt?: string | null
  selected?: boolean | null
  selectionReason?: string | null
  triggerOnPanic?: boolean | null
  zoneName?: string | null
}

export interface OpsSirenRoutingPreview {
  evaluatedSirens: OpsSirenRoutingPreviewItem[]
  heartbeatStaleMinutes?: number | null
  organizationId?: string | null
  plantationId: string
  plantationName?: string | null
  routeScope?: string | null
  sirenCount?: number | null
  targetCount?: number | null
  targetZoneName?: string | null
  targets: OpsSirenRoutingPreviewItem[]
}

export interface OpsMonitoringSnapshot {
  fleetPilotLeads?: OpsMonitoringFleetPilotLead[]
  generatedAt?: string | null
  grievanceCases: OpsMonitoringGrievanceCase[]
  notifications: OpsMonitoringNotificationIssue[]
  postProcessJobs: OpsMonitoringPostProcessIssue[]
  routingGaps: OpsMonitoringRoutingGap[]
  sirenActivations: OpsMonitoringSirenActivation[]
  sirenOnboarding?: OpsMonitoringSirenOnboardingRecord[]
  sirenRegistry?: OpsMonitoringSirenRegistryRecord[]
  scope?: OpsMonitoringScope | null
  success: boolean
  summary?: {
    activeAlerts?: number
    notifications?: {
      deadLetterCount?: number
      failedCount?: number
      retryableCount?: number
      totalProblems?: number
    } | null
    postProcessJobs?: {
      agedCount?: number
      retryableCount?: number
      stalledCount?: number
      totalProblems?: number
    } | null
    routing?: {
      criticalGapCount?: number
      gapCount?: number
      organizationGapCount?: number
    } | null
    sirens?: {
      awaitingFirstCheckInCount?: number
      failedCount?: number
      pendingCount?: number
      recentConfirmedCount?: number
      recentCount?: number
      staleOnboardingCount?: number
    } | null
  } | null
}

export type PartnerDeliveryMode = "manual" | "live"

export type PartnerDeliveryStatus =
  | "prepared"
  | "scheduled"
  | "queued"
  | "processing"
  | "submitted"
  | "acknowledged"
  | "retryable"
  | "delivered"
  | "failed"
  | "dead_letter"
  | "skipped"

export interface GovernanceSummary {
  entitlementKey?: string | null
  governanceLabels?: string[]
  purposeLabel?: string | null
  redactionProfile?: {
    hideMediaUrls?: boolean
    hideMedicalDetails?: boolean
    hidePreciseLocation?: boolean
    hideReporterContact?: boolean
  } | null
  regionKey?: string | null
  regionPolicy?: Record<string, unknown> | null
  retentionPolicyKey?: string | null
  retentionUntil?: string | null
  sensitivityLevel?: string | null
}

export interface IncidentExportPartnerSummary {
  acknowledgedCount?: number
  deadLetterCount?: number
  deliveredCount?: number
  latestActivityAt?: string | null
  latestStatus?: string | null
  liveCount?: number
  pendingRetryCount?: number
  submittedCount?: number
  totalDeliveries?: number
}

export interface IncidentExportSignatureMetadata {
  [key: string]: unknown
  generatedAt?: string | null
  requestedById?: string | null
  signatureVersion?: string | null
}

export interface IncidentExportRecord {
  checksum?: string | null
  completedAt?: string | null
  contractVersion?: string | null
  createdAt?: string | null
  delivery?: {
    legacyMetadata?: Record<string, unknown> | null
    legacyStatus?: string | null
    partnerSummary?: IncidentExportPartnerSummary | null
  } | null
  exportType?: string | null
  format?: string | null
  governance?: GovernanceSummary | null
  id: string
  incidentId?: number | null
  manifest?: Record<string, unknown> | null
  organizationId?: string | null
  partnerDeliveries?: PartnerDeliveryRecord[]
  recipientKey?: string | null
  recipientType?: string | null
  redactionProfile?: GovernanceSummary["redactionProfile"]
  scopeType?: string | null
  signatureMetadata?: IncidentExportSignatureMetadata | null
  status?: string | null
  updatedAt?: string | null
}

export interface PartnerIntegrationRecord {
  adapterKey?: string | null
  authStrategy?: string | null
  baseUrl?: string | null
  callbackConfig?: {
    [key: string]: unknown
    attemptIdField?: string | null
    deliveryKeyField?: string | null
    enabled?: boolean
    partnerReceiptIdField?: string | null
    statusField?: string | null
  } | null
  contractVersion?: string | null
  createdAt?: string | null
  deliveryMode?: PartnerDeliveryMode | null
  deliveryPolicy?: {
    [key: string]: unknown
    deadLetterQueue?: string | null
    deliveryPath?: string | null
    maxAttempts?: number | null
    requestHeaders?: Record<string, string> | null
    retryScheduleMinutes?: number[]
    timeoutMs?: number | null
  } | null
  displayName?: string | null
  endpointType?: string | null
  entitlementKey?: string | null
  governanceProfile?: Record<string, unknown> | null
  id: string
  metadata?: Record<string, unknown> | null
  organizationId?: string | null
  partnerKey?: string | null
  purposeLabel?: string | null
  regionKey?: string | null
  rolloutConfig?: {
    [key: string]: unknown
    activePilotStatuses?: string[]
    autoDispatch?: boolean
    liveEntitlementKey?: string | null
    mode?: string | null
  } | null
  status?: string | null
  updatedAt?: string | null
}

export interface PartnerDeliveryAttemptRecord {
  attemptNumber?: number | null
  callbackEvents?: PartnerDeliveryCallbackEventRecord[]
  createdAt?: string | null
  deliveryId?: string | null
  deliveryKey?: string | null
  execution?: {
    acknowledgedAt?: string | null
    attemptedAt?: string | null
    callbackStatus?: string | null
    completedAt?: string | null
    deadLetteredAt?: string | null
    dispatchLockExpiresAt?: string | null
    dispatchLockedAt?: string | null
    errorCode?: string | null
    errorMessage?: string | null
    externalDeliveryId?: string | null
    failedAt?: string | null
    lastCallbackAt?: string | null
    nextRetryAt?: string | null
    responseCode?: number | null
    scheduledAt?: string | null
    submittedAt?: string | null
  } | null
  exportId?: string | null
  governance?: GovernanceSummary | null
  id: string
  idempotencyKey?: string | null
  incidentId?: number | null
  integration?: PartnerIntegrationRecord | null
  integrationId?: string | null
  liveMode?: PartnerDeliveryMode | null
  manifestRef?: {
    checksum?: string | null
    contractVersion?: string | null
  } | null
  maxAttempts?: number | null
  metadata?: Record<string, unknown> | null
  organizationId?: string | null
  partnerKey?: string | null
  replayedFromAttemptId?: string | null
  requestPayload?: Record<string, unknown> | null
  responsePayload?: Record<string, unknown> | null
  retryOfAttemptId?: string | null
  status?: PartnerDeliveryStatus | null
  updatedAt?: string | null
}

export interface PartnerDeliveryRecord {
  attempts?: PartnerDeliveryAttemptRecord[]
  callbackEvents?: PartnerDeliveryCallbackEventRecord[]
  createdAt?: string | null
  currentAttemptNumber?: number | null
  deliveryKey?: string | null
  execution?: {
    acknowledgedAt?: string | null
    callbackStatus?: string | null
    deadLetterStatus?: string | null
    deadLetteredAt?: string | null
    deliveredAt?: string | null
    externalDeliveryId?: string | null
    failedAt?: string | null
    lastAttemptAt?: string | null
    lastCallbackAt?: string | null
    lastErrorCode?: string | null
    lastErrorMessage?: string | null
    nextRetryAt?: string | null
    submittedAt?: string | null
  } | null
  executionSnapshot?: Record<string, unknown> | null
  exportId?: string | null
  governance?: GovernanceSummary | null
  id: string
  incidentId?: number | null
  integration?: PartnerIntegrationRecord | null
  integrationId?: string | null
  liveMode?: PartnerDeliveryMode | null
  manifestRef?: {
    checksum?: string | null
    contractVersion?: string | null
  } | null
  maxAttempts?: number | null
  metadata?: Record<string, unknown> | null
  organizationId?: string | null
  partnerKey?: string | null
  replayedFromDeliveryId?: string | null
  status?: PartnerDeliveryStatus | null
  updatedAt?: string | null
}

export interface PartnerDeliveryCallbackEventRecord {
  attemptId?: string | null
  callbackStatus?: string | null
  createdAt?: string | null
  deliveryId?: string | null
  deliveryKey?: string | null
  eventKey?: string | null
  eventType?: string | null
  externalDeliveryId?: string | null
  id: string
  integrationId?: string | null
  organizationId?: string | null
  partnerKey?: string | null
  payload?: Record<string, unknown> | null
  processedAt?: string | null
  processingOutcome?: string | null
  receivedAt?: string | null
  requestHeaders?: Record<string, string> | null
  requestPath?: string | null
  updatedAt?: string | null
}

export interface PartnerDeliveryRolloutContext {
  autoDispatch?: boolean
  callbacksEnabled?: boolean
  effectiveMode?: PartnerDeliveryMode | null
  entitlement?: {
    live?: Record<string, unknown> | null
    queue?: Record<string, unknown> | null
  } | null
  integrationMode?: PartnerDeliveryMode | null
  liveAccessLevel?: string | null
  liveAllowed?: boolean
  liveCapable?: boolean
  liveFeatureKey?: string | null
  organizationPilotStatus?: string | null
  pilotEligible?: boolean
  queueAllowed?: boolean
  queueFeatureKey?: string | null
  reasons?: string[]
  requestedMode?: PartnerDeliveryMode | null
  rolloutMode?: string | null
}

export interface ExportDeliveryWorkspaceResponse {
  count: number
  data: PartnerDeliveryRecord[]
  export: IncidentExportRecord
  success: boolean
}

export interface PartnerDeliveryAttemptLedgerResponse {
  count: number
  data: PartnerDeliveryAttemptRecord[]
  delivery: PartnerDeliveryRecord
  success: boolean
}

export interface PartnerDeliveryCallbackEventResponse {
  count: number
  data: PartnerDeliveryCallbackEventRecord[]
  delivery: PartnerDeliveryRecord
  success: boolean
}

export interface IncidentFnolContactProfile {
  [key: string]: unknown
  email?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  licenseNumber?: string | null
  name?: string | null
  phone?: string | null
}

export interface IncidentFnolVehicleProfile {
  [key: string]: unknown
  color?: string | null
  make?: string | null
  model?: string | null
  plateNumber?: string | null
  tag?: string | null
  type?: string | null
  year?: number | string | null
}

export interface IncidentFnolRecord {
  claimReference?: string | null
  contactProfile?: IncidentFnolContactProfile | null
  contractVersion?: string | null
  coverageDetails?: Record<string, unknown> | null
  createdAt?: string | null
  damageSummary?: Record<string, unknown> | null
  driverProfile?: IncidentFnolContactProfile | null
  exportReadiness?: "draft" | "ready" | null
  fleetDetails?: Record<string, unknown> | null
  id?: string | null
  injurySummary?: Record<string, unknown> | null
  insurerCode?: string | null
  insurerName?: string | null
  locationLabel?: string | null
  lossType?: string | null
  metadata?: Record<string, unknown> | null
  mode?: "consumer" | "fleet" | null
  narrative?: string | null
  occurredAt?: string | null
  policyNumber?: string | null
  reportedAt?: string | null
  status?: string | null
  submittedAt?: string | null
  thirdPartyProfile?: Record<string, unknown> | null
  updatedAt?: string | null
  vehicleProfile?: IncidentFnolVehicleProfile | null
}

export interface IncidentFnolBundle {
  fnol: IncidentFnolRecord
  incidentId: number
  missingSections: string[]
  persisted: boolean
}

export interface IncidentEvidenceBundle {
  auditPackage?: Record<string, unknown> | null
  checksum?: string | null
  incidentId: number
}

export interface IncidentExportPreparationResult {
  checksum?: string | null
  contractVersion?: string | null
  exportType?: string | null
  format?: string | null
  governance?: GovernanceSummary | null
  id: string
  manifest?: Record<string, unknown> | null
  recipientKey?: string | null
  recipientType?: string | null
  redactionProfile?: GovernanceSummary["redactionProfile"]
  signatureMetadata?: IncidentExportSignatureMetadata | null
  status?: string | null
}

export interface AiPerformanceMetrics {
  avg_confidence?: number | string | null
  severity_accuracy?: number | string | null
  total_analyzed?: number | null
  type_accuracy?: number | string | null
}

export interface AiPerformanceDailyRecord {
  avg_confidence?: number | string | null
  date: string
  severity_accuracy?: number | string | null
  severity_matches?: number | null
  total?: number | null
  type_accuracy?: number | string | null
  type_matches?: number | null
}

export interface AiPerformanceMismatchRecord {
  ai_severity?: string | null
  ai_type?: string | null
  confidence?: number | null
  id: number
  user_severity?: string | null
  user_type?: string | null
}

export interface AiPerformanceSnapshot {
  daily_performance: AiPerformanceDailyRecord[]
  mismatches_for_review: AiPerformanceMismatchRecord[]
  overall_metrics?: AiPerformanceMetrics | null
  pipeline_status_counts?: Record<string, number> | null
  recent_30_days?: number | null
  total_analyzed?: number | null
}

export interface AiJobRecord {
  completedAt?: string | null
  createdAt?: string | null
  deadLetteredAt?: string | null
  errorCode?: string | null
  errorMessage?: string | null
  failedAt?: string | null
  id: string
  incident?: {
    aiStatus?: string | null
    createdAt?: string | null
    id: number
    severity?: string | null
    type?: string | null
  } | null
  incidentId?: number | null
  maxRetries?: number | null
  nextRetryAt?: string | null
  queueName?: string | null
  replayedFromId?: string | null
  requestedAt?: string | null
  resultSummary?: Record<string, unknown> | null
  retryCount?: number | null
  stage?: string | null
  startedAt?: string | null
  status?: string | null
}

export interface AiInsightRecord {
  aiConfidence?: number | string | null
  aiExtractedData?: string | null
  aiProcessedAt?: string | null
  aiSuggestedSeverity?: string | null
  aiSuggestedType?: string | null
  aiUrgencyScore?: number | null
  ai_extracted?: Record<string, unknown> | null
  ai_keywords?: string[]
  createdAt?: string | null
  description?: string | null
  id: number
  location?: {
    address?: string | null
    name?: string | null
  } | null
  matches?: {
    severity?: boolean
    type?: boolean
  } | null
  severity?: string | null
  type?: string | null
}

export interface AiUrgentIncidentRecord {
  aiUrgencyScore?: number | null
  ai_extracted?: Record<string, unknown> | null
  ai_keywords?: string[]
  id: number
  location?: {
    address?: string | null
    name?: string | null
  } | null
  severity?: string | null
  status?: string | null
  type?: string | null
}

export interface AiKeywordRecord {
  count: number
  keyword: string
}

export interface AiDisagreementRecord {
  ai_classification?: {
    severity?: string | null
    type?: string | null
  } | null
  ai_confidence?: number | null
  ai_urgency_score?: number | null
  created_at?: string | null
  description?: string | null
  disagreement_type?: {
    severity_mismatch?: boolean
    type_mismatch?: boolean
  } | null
  extracted_info?: Record<string, unknown> | null
  id: number
  location?: string | null
  user_classification?: {
    severity?: string | null
    type?: string | null
  } | null
}

export type OperatorIncidentStreamEventType =
  | "connected"
  | "incident_created"
  | "incident_updated"

export interface OperatorIncidentStreamEvent {
  data: {
    createdAt?: string | null
    id?: number | null
    incidentStatus?: string | null
    locationDescription?: string | null
    reportKind?: string | null
    severity?: string | null
    stickerId?: string | null
    type?: string | null
  }
  eventType: OperatorIncidentStreamEventType
}

interface ApiRequestOptions extends Omit<RequestInit, "body" | "headers"> {
  auth?: ApiAuthMode
  body?: BodyInit | null | object
  headers?: HeadersInit
}

export class ApiError extends Error {
  readonly status: number
  readonly responseBody: unknown

  constructor(message: string, status: number, responseBody: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.responseBody = responseBody
  }
}

function resolveAuthorizationHeader(auth: ApiAuthMode) {
  if (auth === "operator") {
    return getOperatorAuthorizationHeader()
  }

  if (auth === "owner") {
    return getOwnerPortalAuthorizationHeader()
  }

  return null
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function normalizeRequestBody(body: ApiRequestOptions["body"]) {
  if (body === null || body === undefined) {
    return null
  }

  if (
    typeof body === "string"
    || body instanceof FormData
    || body instanceof URLSearchParams
    || body instanceof Blob
    || body instanceof ArrayBuffer
  ) {
    return body
  }

  return JSON.stringify(body)
}

async function parseResponseBody(response: Response) {
  const contentType = response.headers.get("content-type") ?? ""

  if (contentType.includes("application/json")) {
    return response.json()
  }

  return response.text()
}

function getFailureMessage(responseBody: unknown, status: number) {
  if (isPlainObject(responseBody)) {
    const message =
      typeof responseBody.error === "string"
        ? responseBody.error
        : typeof responseBody.message === "string"
          ? responseBody.message
          : null

    if (message) {
      return message
    }
  }

  if (typeof responseBody === "string" && responseBody.trim()) {
    return responseBody
  }

  return `Request failed with status ${status}`
}

function unwrapEnvelope<
  TData,
  TEnvelope extends ApiEnvelope<TData> = ApiEnvelope<TData>,
>(responseBody: unknown): TEnvelope {
  if (!isPlainObject(responseBody)) {
    throw new Error("Expected a JSON response object from the backend.")
  }

  if (responseBody.success === false) {
    throw new Error(getFailureMessage(responseBody, 200))
  }

  if (!("data" in responseBody)) {
    throw new Error("Expected the backend response to include a data payload.")
  }

  return responseBody as TEnvelope
}

function buildLookupQuery(params: PublicLookupQuery) {
  const searchParams = new URLSearchParams()

  searchParams.set("tag", params.tag)

  if (params.intent) {
    searchParams.set("intent", params.intent)
  }

  if (params.gpsLatitude) {
    searchParams.set("gpsLatitude", params.gpsLatitude)
  }

  if (params.gpsLongitude) {
    searchParams.set("gpsLongitude", params.gpsLongitude)
  }

  if (params.gpsAccuracy) {
    searchParams.set("gpsAccuracy", params.gpsAccuracy)
  }

  if (params.phone) {
    searchParams.set("phone", params.phone)
  }

  return searchParams.toString()
}

function buildSearchParams(
  params: Record<string, number | string | boolean | undefined | null>
) {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue
    }

    searchParams.set(key, String(value))
  }

  return searchParams.toString()
}

async function request<TResponse>(
  path: string,
  { auth = "none", body, headers, method = "GET", ...init }: ApiRequestOptions = {}
): Promise<TResponse> {
  const url = path.startsWith("/api/") ? path : `/api${path}`
  const requestHeaders = new Headers(headers)
  const normalizedBody = normalizeRequestBody(body)
  const authorizationHeader = resolveAuthorizationHeader(auth)

  requestHeaders.set("Accept", "application/json")

  if (authorizationHeader) {
    requestHeaders.set("Authorization", authorizationHeader)
  }

  if (
    normalizedBody !== null
    && !(normalizedBody instanceof FormData)
    && !requestHeaders.has("Content-Type")
  ) {
    requestHeaders.set("Content-Type", "application/json")
  }

  return Sentry.startSpan(
    {
      name: `${method} ${url}`,
      op: "http.client",
      attributes: {
        "http.method": method,
        "http.url": url,
      },
    },
    async () => {
      const response = await fetch(url, {
        ...init,
        method,
        body: normalizedBody,
        headers: requestHeaders,
        credentials: "include",
      })

      const responseBody = await parseResponseBody(response)

      Sentry.addBreadcrumb({
        category: "http",
        type: "http",
        level: response.ok ? "info" : "error",
        data: {
          url,
          method,
          statusCode: response.status,
        },
      })

      if (!response.ok) {
        const error = new ApiError(
          getFailureMessage(responseBody, response.status),
          response.status,
          responseBody
        )

        Sentry.captureException(error, {
          tags: {
            area: "v2-api-client",
          },
          extra: {
            method,
            url,
            responseBody,
          },
        })

        throw error
      }

      return responseBody as TResponse
    }
  )
}

export const apiClient = {
  getHealth() {
    return request<BackendHealthResponse>("/health")
  },
  async getIncidentEscalationPacket(token: string) {
    const responseBody = await request<ApiEnvelope<IncidentEscalationPacketPublicPayload>>(
      `/incident-escalation-packets/${encodeURIComponent(token)}`
    )

    return unwrapEnvelope<IncidentEscalationPacketPublicPayload>(responseBody)
  },
  async lookupPublicTag(params: PublicLookupQuery) {
    const query = buildLookupQuery(params)
    const responseBody = await request<ApiEnvelope<PublicLookupData>>(
      `/vehicles/lookup?${query}`
    )

    return unwrapEnvelope<PublicLookupData>(responseBody)
  },
  async getActiveConsentNotices(params: ActiveNoticeQuery = {}) {
    const query = buildSearchParams({
      audience: params.audience,
      locale: params.locale,
      surface: params.surface,
    })
    const responseBody = await request<ApiEnvelope<ConsentNoticeVersionSummary[]>>(
      `/vehicles/compliance/notices/active${query ? `?${query}` : ""}`
    )

    return unwrapEnvelope<ConsentNoticeVersionSummary[]>(responseBody)
  },
  async startPublicOtp(payload: {
    organizationId?: string
    phoneNumber: string
    stickerId: string
    tag: string
    tagType: TagType
  }) {
    const responseBody = await request<ApiEnvelope<PublicOtpStartResponse>>(
      "/auth/otp/start",
      {
        body: payload,
        headers: {
          "X-Session-Id": getOrCreateRegistrationSessionId(),
        },
        method: "POST",
      }
    )

    return unwrapEnvelope<PublicOtpStartResponse>(responseBody)
  },
  async verifyPublicOtp(payload: {
    otp: string
    phoneNumber: string
    verificationId: string
  }) {
    const responseBody = await request<ApiEnvelope<PublicOtpVerifyResponse>>(
      "/auth/otp/verify",
      {
        body: payload,
        headers: {
          "X-Session-Id": getOrCreateRegistrationSessionId(),
        },
        method: "POST",
      }
    )

    return unwrapEnvelope<PublicOtpVerifyResponse>(responseBody)
  },
  async submitPublicRegistration(payload: PublicRegistrationRequest) {
    const responseBody = await request<ApiEnvelope<PublicLookupData>>(
      "/vehicles/register",
      {
        body: payload,
        headers: {
          "X-Session-Id": getOrCreateRegistrationSessionId(),
        },
        method: "POST",
      }
    )

    return unwrapEnvelope<PublicLookupData>(responseBody)
  },
  async submitPublicZoneName(payload: PublicZoneNamingRequest) {
    const responseBody = await request<ApiEnvelope<PublicLookupData>>(
      "/vehicles/zone-name",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PublicLookupData>(responseBody)
  },
  async submitPublicIncident(payload: PublicIncidentRequest) {
    const responseBody = await request<ApiEnvelope<PublicIncidentResponse>>(
      "/incidents",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PublicIncidentResponse>(responseBody)
  },
  async startOwnerPortalOtp(payload: {
    phoneNumber: string
    tag: string
    tagType?: TagType | null
  }) {
    const responseBody = await request<ApiEnvelope<OwnerPortalOtpStartResponse>>(
      "/auth/otp/start",
      {
        body: {
          phoneNumber: payload.phoneNumber,
          purpose: "owner_portal",
          tag: payload.tag,
          ...(payload.tagType ? { tagType: payload.tagType } : {}),
        },
        headers: {
          "X-Device-Id": getOrCreateOwnerPortalDeviceId(),
        },
        method: "POST",
      }
    )

    return unwrapEnvelope<OwnerPortalOtpStartResponse>(responseBody)
  },
  async verifyOwnerPortalOtp(payload: {
    otp: string
    phoneNumber: string
    verificationId: string
  }) {
    const responseBody = await request<ApiEnvelope<OwnerPortalOtpVerifyResponse>>(
      "/auth/otp/verify",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OwnerPortalOtpVerifyResponse>(responseBody)
  },
  async getOwnerPortalProfile() {
    const responseBody = await request<ApiEnvelope<OwnerPortalProfile>>(
      "/vehicles/owner",
      {
        auth: "owner",
      }
    )

    return unwrapEnvelope<OwnerPortalProfile>(responseBody)
  },
  async updateOwnerPortalProfile(payload: OwnerProfileUpdateRequest) {
    const responseBody = await request<OwnerPortalProfileUpdateEnvelope>(
      "/vehicles/owner",
      {
        auth: "owner",
        body: payload,
        method: "PATCH",
      }
    )

    return unwrapEnvelope<OwnerPortalProfile, OwnerPortalProfileUpdateEnvelope>(
      responseBody
    )
  },
  async getOwnerPortalIncidents() {
    const responseBody = await request<ApiEnvelope<OwnerIncidentRecord[]>>(
      "/incidents/owner",
      {
        auth: "owner",
      }
    )

    return unwrapEnvelope<OwnerIncidentRecord[]>(responseBody)
  },
  async createOwnerPrivacyRequest(payload: PrivacyRequestCreatePayload) {
    const responseBody = await request<
      ApiEnvelope<{
        compliance?: ComplianceSummary | null
        request: PrivacyRequestRecord
      }>
    >("/vehicles/privacy-requests", {
      auth: "owner",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope(responseBody)
  },
  async listOwnerPrivacyRequests() {
    const responseBody = await request<ApiEnvelope<PrivacyRequestRecord[]>>(
      "/vehicles/privacy-requests",
      {
        auth: "owner",
      }
    )

    return unwrapEnvelope<PrivacyRequestRecord[]>(responseBody)
  },
  async submitPublicGrievance(payload: PublicGrievanceCreatePayload) {
    const responseBody = await request<ApiEnvelope<GrievanceCaseRecord>>(
      "/vehicles/grievances",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<GrievanceCaseRecord>(responseBody)
  },
  async submitFleetPilotLead(payload: FleetPilotLeadCreatePayload) {
    const responseBody = await request<ApiEnvelope<FleetPilotLeadRecord>>(
      "/fleet/pilot-leads",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetPilotLeadRecord>(responseBody)
  },
  async createSecurityIncident(payload: SecurityIncidentCreatePayload) {
    const responseBody = await request<ApiEnvelope<SecurityIncidentRecord>>(
      "/vehicles/security-incidents",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<SecurityIncidentRecord>(responseBody)
  },
  async updateSecurityIncident(
    incidentId: string,
    payload: SecurityIncidentUpdatePayload
  ) {
    const responseBody = await request<ApiEnvelope<SecurityIncidentRecord>>(
      `/vehicles/security-incidents/${encodeURIComponent(incidentId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PATCH",
      }
    )

    return unwrapEnvelope<SecurityIncidentRecord>(responseBody)
  },
  async getCurrentSession() {
    const responseBody = await request<ApiEnvelope<OperatorCurrentSession>>(
      "/session/me",
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorCurrentSession>(responseBody)
  },
  async getAccountOnboarding() {
    const responseBody = await request<ApiEnvelope<AccountOnboardingRecord>>(
      "/session/onboarding",
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<AccountOnboardingRecord>(responseBody)
  },
  async updateAccountOnboarding(payload: AccountOnboardingUpdateRequest) {
    const responseBody = await request<ApiEnvelope<AccountOnboardingRecord>>(
      "/session/onboarding",
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<AccountOnboardingRecord>(responseBody)
  },
  async getOperatorUserProfile() {
    const session = await this.getCurrentSession()

    return {
      data: session.data.user,
      success: session.success,
    }
  },
  async getOperatorAuthorityProfile() {
    const responseBody = await request<ApiEnvelope<OperatorAuthorityProfile>>(
      "/authorities/me",
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorAuthorityProfile>(responseBody)
  },
  async getOrganizationJurisdictions(organizationId: string) {
    const responseBody = await request<
      ApiEnvelope<AuthorityJurisdictionSummary[]>
    >(`/organizations/${organizationId}/jurisdictions`, {
      auth: "operator",
    })

    return unwrapEnvelope<AuthorityJurisdictionSummary[]>(responseBody)
  },
  async getOperatorIncidents(params: OperatorIncidentListQuery = {}) {
    const query = buildSearchParams({
      agencyKind: params.agencyKind,
      behaviorType: params.behaviorType,
      incidentStatus: params.incidentStatus,
      jurisdictionId: params.jurisdictionId,
      limit: params.limit,
      locationId: params.locationId,
      offset: params.offset,
      reportKind: params.reportKind,
      severity: params.severity,
      status: params.status,
    })
    const responseBody = await request<ApiEnvelope<OperatorIncidentRecord[]>>(
      `/incidents${query ? `?${query}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorIncidentRecord[]>(responseBody)
  },
  async getOperatorIncidentStats(organizationId?: string) {
    const query = buildSearchParams({
      organizationId,
    })
    const responseBody = await request<
      ApiEnvelope<{
        by_ai_status?: Array<{ _count: number; aiStatus: string }>
        by_fraud_status?: Array<{ _count: number; fraudStatus: string }>
        by_incident_status?: Array<{ _count: number; incidentStatus: string }>
        by_report_kind?: Array<{ _count: number; reportKind: string }>
        by_severity?: Array<{ _count: number; severity: string }>
        by_type?: Array<{ _count: number; type: string }>
        by_verification_status?: Array<{
          _count: number
          verificationStatus: string
        }>
        critical_or_severe_open?: number
        in_progress?: number
        needs_attention_now?: number
        open?: number
        pending?: number
        recent_24h?: number
        resolved?: number
        total?: number
      }>
    >(`/incidents/stats/summary${query ? `?${query}` : ""}`, {
      auth: "operator",
    })

    return unwrapEnvelope<{
      by_ai_status?: Array<{ _count: number; aiStatus: string }>
      by_fraud_status?: Array<{ _count: number; fraudStatus: string }>
      by_incident_status?: Array<{ _count: number; incidentStatus: string }>
      by_report_kind?: Array<{ _count: number; reportKind: string }>
      by_severity?: Array<{ _count: number; severity: string }>
      by_type?: Array<{ _count: number; type: string }>
      by_verification_status?: Array<{
        _count: number
        verificationStatus: string
      }>
      critical_or_severe_open?: number
      in_progress?: number
      needs_attention_now?: number
      open?: number
      pending?: number
      recent_24h?: number
      resolved?: number
      total?: number
    }>(responseBody)
  },
  async getOperatorIncident(incidentId: number) {
    const responseBody = await request<ApiEnvelope<OperatorIncidentRecord>>(
      `/incidents/${incidentId}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorIncidentRecord>(responseBody)
  },
  async getOperatorIncidentTimeline(incidentId: number) {
    const responseBody = await request<ApiEnvelope<OperatorTimelineEntry[]>>(
      `/incidents/${incidentId}/timeline`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorTimelineEntry[]>(responseBody)
  },
  async getOperatorIncidentEvidence(incidentId: number) {
    const responseBody = await request<
      ApiEnvelope<{
        auditPackage?: Record<string, unknown> | null
        checksum?: string | null
        incidentId: number
      }>
    >(`/incidents/${incidentId}/evidence`, {
      auth: "operator",
    })

    return unwrapEnvelope<{
      auditPackage?: Record<string, unknown> | null
      checksum?: string | null
      incidentId: number
    }>(responseBody)
  },
  async updateOperatorIncidentStatus(
    incidentId: number,
    payload: OperatorIncidentStatusUpdate
  ) {
    const responseBody = await request<ApiEnvelope<OperatorIncidentRecord>>(
      `/incidents/${incidentId}/status`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<OperatorIncidentRecord>(responseBody)
  },
  async createOperatorIncidentNote(
    incidentId: number,
    payload: OperatorIncidentNoteCreate
  ) {
    const responseBody = await request<ApiEnvelope<OperatorIncidentUpdateRecord>>(
      `/incidents/${incidentId}/updates`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OperatorIncidentUpdateRecord>(responseBody)
  },
  async updateOperatorAssignment(
    incidentId: number,
    assignmentId: string,
    payload: OperatorAssignmentUpdate
  ) {
    const responseBody = await request<
      ApiEnvelope<{
        assignment: OperatorAssignmentRecord
        incident: OperatorIncidentRecord | null
      }>
    >(`/incidents/${incidentId}/assignments/${assignmentId}`, {
      auth: "operator",
      body: payload,
      method: "PUT",
    })

    return unwrapEnvelope<{
      assignment: OperatorAssignmentRecord
      incident: OperatorIncidentRecord | null
    }>(responseBody)
  },
  async shareOperatorIncidentEscalationPacket(
    incidentId: number,
    payload: {
      channels?: Array<"email" | "sms" | "whatsapp">
      forceRefresh?: boolean
      reason?: string
    } = {}
  ) {
    const responseBody = await request<ApiEnvelope<OperatorIncidentEscalationShareResponse>>(
      `/incidents/${incidentId}/iep/share`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OperatorIncidentEscalationShareResponse>(responseBody)
  },
  async revokeOperatorIncidentEscalationPacket(incidentId: number, reason?: string) {
    const responseBody = await request<ApiEnvelope<OperatorIncidentEscalationRevokeResponse>>(
      `/incidents/${incidentId}/iep/revoke`,
      {
      auth: "operator",
      body: {
        reason,
      },
      method: "POST",
      }
    )

    return unwrapEnvelope<OperatorIncidentEscalationRevokeResponse>(responseBody)
  },
  async getOperatorPerformance(organizationId?: string) {
    const query = buildSearchParams({
      organizationId,
    })
    const responseBody = await request<ApiEnvelope<OperatorPerformanceMetrics>>(
      `/analytics/performance${query ? `?${query}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorPerformanceMetrics>(responseBody)
  },
  async getOperatorResponseTimes(limit = 120) {
    const query = buildSearchParams({ limit })
    const responseBody = await request<
      ApiEnvelope<OperatorResponseTimesRecord[]>
    >(`/analytics/response-times?${query}`, {
      auth: "operator",
    })

    return unwrapEnvelope<OperatorResponseTimesRecord[]>(responseBody)
  },
  async getOperatorBreakdown(organizationId?: string, days?: number) {
    const query = buildSearchParams({
      days,
      organizationId,
    })
    const responseBody = await request<ApiEnvelope<OperatorBreakdownMetrics>>(
      `/analytics/breakdown${query ? `?${query}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorBreakdownMetrics>(responseBody)
  },
  async getOperatorTimeline(period = "30d", organizationId?: string) {
    const query = buildSearchParams({
      organizationId,
      period,
    })
    const responseBody = await request<
      ApiEnvelope<{
        period?: string | null
        timeline: OperatorTimelinePoint[]
        total?: number | null
      }>
    >(`/analytics/timeline?${query}`, {
      auth: "operator",
    })

    return unwrapEnvelope<{
      period?: string | null
      timeline: OperatorTimelinePoint[]
      total?: number | null
    }>(responseBody)
  },
  async getOperatorHourlyDistribution(days = 30, organizationId?: string) {
    const query = buildSearchParams({
      days,
      organizationId,
    })
    const responseBody = await request<
      ApiEnvelope<Array<{ count: number; hour: number }>>
    >(`/analytics/hourly-distribution?${query}`, {
      auth: "operator",
    })

    return unwrapEnvelope<Array<{ count: number; hour: number }>>(responseBody)
  },
  async getOperatorSafetyScores(
    days = 30,
    limit = 8,
    organizationId?: string
  ) {
    const query = buildSearchParams({
      days,
      limit,
      organizationId,
    })
    const responseBody = await request<
      ApiEnvelope<{
        items: OperatorSafetyScoreItem[]
        periodDays?: number | null
      }>
    >(`/analytics/safety-scores?${query}`, {
      auth: "operator",
    })

    return unwrapEnvelope<{
      items: OperatorSafetyScoreItem[]
      periodDays?: number | null
    }>(responseBody)
  },
  async getOperatorHotspots(days = 30, limit = 8, organizationId?: string) {
    const query = buildSearchParams({
      days,
      limit,
      organizationId,
    })
    const responseBody = await request<
      ApiEnvelope<{
        hotspots: OperatorHotspotItem[]
        periodDays?: number | null
      }>
    >(`/analytics/hotspots?${query}`, {
      auth: "operator",
    })

    return unwrapEnvelope<{
      hotspots: OperatorHotspotItem[]
      periodDays?: number | null
    }>(responseBody)
  },
  async getFleetInventory(limit = 200) {
    const query = buildSearchParams({
      limit,
      tagType: "fleet_tag",
    })
    const responseBody = await request<ApiEnvelope<PublicLookupData[]>>(
      `/vehicles?${query}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PublicLookupData[]>(responseBody)
  },
  async registerFleetQrCodes(payload: FleetQrRegisterRequest) {
    const responseBody = await request<ApiEnvelope<FleetQrRegisterResponse>>(
      "/fleet/register",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetQrRegisterResponse>(responseBody)
  },
  async provisionFleetCustomer(payload: FleetCustomerProvisioningRequest) {
    const responseBody = await request<ApiEnvelope<FleetCustomerProvisioningResponse>>(
      "/fleet/provisioning/customers",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetCustomerProvisioningResponse>(responseBody).data
  },
  async getFleetProvisioningLeads(limit = 24) {
    const search = buildSearchParams({
      limit,
    })
    const responseBody = await request<ApiEnvelope<FleetPilotLeadRecord[]>>(
      `/fleet/provisioning/leads${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetPilotLeadRecord[]>(responseBody).data
  },
  async getFleetAuthorityDirectory() {
    const responseBody = await request<ApiEnvelope<FleetAuthorityDirectoryRecord[]>>(
      "/fleet/authority-directory",
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetAuthorityDirectoryRecord[]>(responseBody).data
  },
  async resolveFleetOnboardingRun(payload: FleetOnboardingRunResolveRequest) {
    const responseBody = await request<ApiEnvelope<FleetOnboardingRunResolveResponse>>(
      "/fleet/onboarding/runs/resolve",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetOnboardingRunResolveResponse>(responseBody).data
  },
  async issueFleetKitBatch(payload: FleetKitIssueRequest) {
    const responseBody = await request<ApiEnvelope<FleetKitIssueResponse>>(
      "/fleet/kits/issue",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetKitIssueResponse>(responseBody).data
  },
  async persistFleetOnboardingMasters(payload: FleetOnboardingPersistRequest) {
    const responseBody = await request<ApiEnvelope<FleetOnboardingPersistResponse>>(
      "/fleet/onboarding/persist",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetOnboardingPersistResponse>(responseBody).data
  },
  async getFleetKitBatch(batchCode: string) {
    const responseBody = await request<ApiEnvelope<FleetKitBatchRecord>>(
      `/fleet/kits/batches/${encodeURIComponent(batchCode)}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetKitBatchRecord>(responseBody).data
  },
  getFleetKitBatchZipDownloadPath(batchCode: string) {
    return `/api/fleet/kits/batches/${encodeURIComponent(batchCode)}/download.zip`
  },
  async getFleetOperatingUnits(query: {
    organizationId: string
  }) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<FleetOperatingUnitRecord[]>>(
      `/fleet/operating-units?${search}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetOperatingUnitRecord[]>(responseBody).data
  },
  async createFleetOperatingUnit(payload: FleetOperatingUnitUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetOperatingUnitRecord>>(
      "/fleet/operating-units",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetOperatingUnitRecord>(responseBody).data
  },
  async updateFleetOperatingUnit(unitId: string, payload: FleetOperatingUnitUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetOperatingUnitRecord>>(
      `/fleet/operating-units/${encodeURIComponent(unitId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<FleetOperatingUnitRecord>(responseBody).data
  },
  async deleteFleetOperatingUnit(unitId: string, organizationId: string) {
    const search = buildSearchParams({ organizationId })
    const responseBody = await request<ApiEnvelope<{ id: string }>>(
      `/fleet/operating-units/${encodeURIComponent(unitId)}?${search}`,
      {
        auth: "operator",
        method: "DELETE",
      }
    )

    return unwrapEnvelope<{ id: string }>(responseBody).data
  },
  async getFleetAssets(query: {
    organizationId: string
    vertical?: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
      vertical: query.vertical,
    })
    const responseBody = await request<ApiEnvelope<FleetAssetRecord[]>>(
      `/fleet/assets?${search}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetAssetRecord[]>(responseBody).data
  },
  async createFleetAsset(payload: FleetAssetUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetAssetRecord>>(
      "/fleet/assets",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetAssetRecord>(responseBody).data
  },
  async updateFleetAsset(assetId: string, payload: FleetAssetUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetAssetRecord>>(
      `/fleet/assets/${encodeURIComponent(assetId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<FleetAssetRecord>(responseBody).data
  },
  async deleteFleetAsset(assetId: string, organizationId: string) {
    const search = buildSearchParams({ organizationId })
    const responseBody = await request<ApiEnvelope<{ id: string }>>(
      `/fleet/assets/${encodeURIComponent(assetId)}?${search}`,
      {
        auth: "operator",
        method: "DELETE",
      }
    )

    return unwrapEnvelope<{ id: string }>(responseBody).data
  },
  async getFleetPeople(query: {
    organizationId: string
    vertical?: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
      vertical: query.vertical,
    })
    const responseBody = await request<ApiEnvelope<FleetPersonRecord[]>>(
      `/fleet/people?${search}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetPersonRecord[]>(responseBody).data
  },
  async createFleetPerson(payload: FleetPersonUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetPersonRecord>>(
      "/fleet/people",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetPersonRecord>(responseBody).data
  },
  async updateFleetPerson(personId: string, payload: FleetPersonUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetPersonRecord>>(
      `/fleet/people/${encodeURIComponent(personId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<FleetPersonRecord>(responseBody).data
  },
  async deleteFleetPerson(personId: string, organizationId: string) {
    const search = buildSearchParams({ organizationId })
    const responseBody = await request<ApiEnvelope<{ id: string }>>(
      `/fleet/people/${encodeURIComponent(personId)}?${search}`,
      {
        auth: "operator",
        method: "DELETE",
      }
    )

    return unwrapEnvelope<{ id: string }>(responseBody).data
  },
  async getFleetAssignments(query: {
    organizationId: string
    vertical?: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
      vertical: query.vertical,
    })
    const responseBody = await request<ApiEnvelope<FleetAssignmentRecord[]>>(
      `/fleet/assignments?${search}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetAssignmentRecord[]>(responseBody).data
  },
  async createFleetAssignment(payload: FleetAssignmentUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetAssignmentRecord>>(
      "/fleet/assignments",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetAssignmentRecord>(responseBody).data
  },
  async updateFleetAssignment(assignmentId: string, payload: FleetAssignmentUpsertRequest) {
    const responseBody = await request<ApiEnvelope<FleetAssignmentRecord>>(
      `/fleet/assignments/${encodeURIComponent(assignmentId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<FleetAssignmentRecord>(responseBody).data
  },
  async deleteFleetAssignment(assignmentId: string, organizationId: string) {
    const search = buildSearchParams({ organizationId })
    const responseBody = await request<ApiEnvelope<{ id: string }>>(
      `/fleet/assignments/${encodeURIComponent(assignmentId)}?${search}`,
      {
        auth: "operator",
        method: "DELETE",
      }
    )

    return unwrapEnvelope<{ id: string }>(responseBody).data
  },
  async importFleetRosterCsv(payload: {
    csv: string
    dryRun?: boolean
    generateTags?: boolean
    organizationId?: string | null
  }) {
    const responseBody = await request<ApiEnvelope<FleetCsvImportSummary>>(
      "/vehicles/import?tagType=fleet_tag",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetCsvImportSummary>(responseBody)
  },
  async uploadFleetOnboardingRows(payload: FleetOnboardingUploadRequest) {
    const responseBody = await request<ApiEnvelope<FleetOnboardingUploadResponse>>(
      "/fleet/onboarding/import",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<FleetOnboardingUploadResponse>(responseBody).data
  },
  async getFleetCertifications(query: FleetCertificationQuery = {}) {
    const search = buildSearchParams({
      includeEvidence: query.includeEvidence ?? true,
      limit: query.limit ?? 200,
      organizationId: query.organizationId,
      status: query.status,
    })
    const responseBody = await request<ApiEnvelope<FleetCertificationRecord[]>>(
      `/ecosystem/fleet-certifications?${search}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<FleetCertificationRecord[]>(responseBody)
  },
  async getOrganizations() {
    const responseBody = await request<ApiEnvelope<OperatorOrganizationRecord[]>>(
      "/organizations",
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorOrganizationRecord[]>(responseBody)
  },
  async getOrganization(organizationId: string) {
    const responseBody = await request<ApiEnvelope<OperatorOrganizationDetail>>(
      `/organizations/${organizationId}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorOrganizationDetail>(responseBody)
  },
  async getOrchards(query: { organizationId?: string } = {}) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<OrchardRecord[]>>(
      `/orchards${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OrchardRecord[]>(responseBody)
  },
  async getOrchard(
    orchardId: string,
    query: { organizationId?: string } = {}
  ) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<OrchardDetailRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OrchardDetailRecord>(responseBody)
  },
  async createOrchard(payload: OrchardCreateRequest) {
    const responseBody = await request<ApiEnvelope<OrchardRecord>>("/orchards", {
      auth: "operator",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope<OrchardRecord>(responseBody)
  },
  async updateOrchard(orchardId: string, payload: OrchardUpdateRequest) {
    const responseBody = await request<ApiEnvelope<OrchardRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<OrchardRecord>(responseBody)
  },
  async getOrchardTrees(
    orchardId: string,
    query: {
      blockId?: string
      organizationId?: string
      status?: string
    } = {}
  ) {
    const search = buildSearchParams({
      blockId: query.blockId,
      organizationId: query.organizationId,
      status: query.status,
    })
    const responseBody = await request<ApiEnvelope<OrchardTreeRecord[]>>(
      `/orchards/${encodeURIComponent(orchardId)}/trees${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OrchardTreeRecord[]>(responseBody)
  },
  async getOrchardAnalytics(
    orchardId: string,
    query: {
      dateFrom?: string
      dateTo?: string
      organizationId?: string
    } = {}
  ) {
    const search = buildSearchParams({
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<OrchardAnalyticsRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}/analytics${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OrchardAnalyticsRecord>(responseBody)
  },
  async getOrchardPortfolioAnalytics(
    query: {
      dateFrom?: string
      dateTo?: string
      organizationId?: string
    } = {}
  ) {
    const search = buildSearchParams({
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<OrchardPortfolioAnalyticsRecord>>(
      `/orchards/analytics/portfolio${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OrchardPortfolioAnalyticsRecord>(responseBody)
  },
  async createOrchardBlock(
    orchardId: string,
    payload: OrchardBlockCreateRequest & { organizationId?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<OrchardBlockRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}/blocks`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OrchardBlockRecord>(responseBody)
  },
  async createOrchardRow(
    orchardId: string,
    payload: OrchardRowCreateRequest & { organizationId?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<OrchardRowRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}/rows`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OrchardRowRecord>(responseBody)
  },
  async createOrchardTree(
    orchardId: string,
    payload: OrchardTreeCreateRequest & { organizationId?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<OrchardTreeRecord>>(
      `/orchards/${encodeURIComponent(orchardId)}/trees`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OrchardTreeRecord>(responseBody)
  },
  async importOrchardTrees(
    orchardId: string,
    payload: {
      csv: string
      dryRun?: boolean
      organizationId?: string | null
    }
  ) {
    const responseBody = await request<ApiEnvelope<OrchardTreeImportSummary>>(
      `/orchards/${encodeURIComponent(orchardId)}/trees/import`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OrchardTreeImportSummary>(responseBody)
  },
  async lookupOrchardTreeByToken(token: string) {
    const search = buildSearchParams({
      token,
    })
    const responseBody = await request<ApiEnvelope<OrchardTreeRecord>>(
      `/orchards/tree-lookup?${search}`
    )

    return unwrapEnvelope<OrchardTreeRecord>(responseBody)
  },
  async getOrchardTreeHistoryByToken(token: string, limit = 10) {
    const search = buildSearchParams({
      limit,
      token,
    })
    const responseBody = await request<ApiEnvelope<OrchardObservationRecord[]>>(
      `/orchards/tree-lookup/history?${search}`
    )

    return unwrapEnvelope<OrchardObservationRecord[]>(responseBody)
  },
  async createOrchardObservation(payload: OrchardObservationCreateRequest) {
    const responseBody = await request<ApiEnvelope<OrchardObservationRecord>>(
      "/orchards/observations",
      {
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OrchardObservationRecord>(responseBody)
  },
  async createOrganization(payload: OperatorOrganizationCreateRequest) {
    const responseBody = await request<ApiEnvelope<OperatorOrganizationDetail>>(
      "/organizations",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<OperatorOrganizationDetail>(responseBody)
  },
  async updateOrganization(
    organizationId: string,
    payload: OperatorOrganizationUpdateRequest
  ) {
    const responseBody = await request<ApiEnvelope<OperatorOrganizationDetail>>(
      `/organizations/${organizationId}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<OperatorOrganizationDetail>(responseBody)
  },
  async getPlantationPolicy(
    plantationId: string,
    query: { organizationId?: string } = {}
  ) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<PlantationPolicyRecord>>(
      `/vehicles/plantation-policies/${encodeURIComponent(plantationId)}${
        search ? `?${search}` : ""
      }`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PlantationPolicyRecord>(responseBody)
  },
  async updatePlantationPolicy(
    plantationId: string,
    payload: PlantationPolicyUpdateRequest & { organizationId?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<PlantationPolicyRecord>>(
      `/vehicles/plantation-policies/${encodeURIComponent(plantationId)}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<PlantationPolicyRecord>(responseBody)
  },
  async getPlantationSirens(
    plantationId: string,
    query: { organizationId?: string } = {}
  ) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord[]>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}${
        search ? `?${search}` : ""
      }`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord[]>(responseBody)
  },
  async createPlantationSiren(
    plantationId: string,
    payload: PlantationSirenUpsertRequest
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async updatePlantationSiren(
    plantationId: string,
    sirenId: string,
    payload: PlantationSirenUpsertRequest
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async deletePlantationSiren(
    plantationId: string,
    sirenId: string,
    query: { organizationId?: string } = {}
  ) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<
      ApiEnvelope<{ id: string; organizationId?: string | null; plantationId: string }>
    >(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}${search ? `?${search}` : ""}`,
      {
        auth: "operator",
        method: "DELETE",
      }
    )

    return unwrapEnvelope<{ id: string; organizationId?: string | null; plantationId: string }>(
      responseBody
    )
  },
  async recordPlantationSirenHeartbeat(
    plantationId: string,
    sirenId: string,
    payload: { organizationId?: string | null } = {}
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/heartbeat`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async rotatePlantationSirenHeartbeatKey(
    plantationId: string,
    sirenId: string,
    payload: { organizationId?: string | null } = {}
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/heartbeat-key/rotate`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async setPlantationSirenMaintenance(
    plantationId: string,
    sirenId: string,
    payload: { enabled: boolean; organizationId?: string | null; reason?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/maintenance`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async schedulePlantationSirenMaintenance(
    plantationId: string,
    sirenId: string,
    payload: {
      note?: string | null
      organizationId?: string | null
      scheduledEndAt: string
      scheduledStartAt: string
    }
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/maintenance/schedule`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async addPlantationSirenMaintenanceNote(
    plantationId: string,
    sirenId: string,
    payload: { note: string; organizationId?: string | null }
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/maintenance/note`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async getPlantationSirenMaintenanceHistory(
    plantationId: string,
    sirenId: string,
    query: { organizationId?: string | null } = {}
  ) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<PlantationSirenMaintenanceEventRecord[]>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/maintenance-history${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PlantationSirenMaintenanceEventRecord[]>(responseBody)
  },
  async restorePlantationSirenService(
    plantationId: string,
    sirenId: string,
    payload: { organizationId?: string | null } = {}
  ) {
    const responseBody = await request<ApiEnvelope<PlantationSirenRecord>>(
      `/vehicles/plantation-sirens/${encodeURIComponent(plantationId)}/${encodeURIComponent(
        sirenId
      )}/restore-service`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<PlantationSirenRecord>(responseBody)
  },
  async getCards(query: {
    organizationId?: string
  } = {}) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<OperatorCardRecord[]>>(
      `/cards${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<OperatorCardRecord[]>(responseBody)
  },
  async getB2cStockBatches(query: B2cStockBatchQuery = {}) {
    const search = buildSearchParams({
      kitVariant: query.kitVariant,
      lifecycleState: query.lifecycleState,
      limit: query.limit ?? 50,
      organizationId: query.organizationId,
    })
    const responseBody = await request<ApiEnvelope<B2cStockBatchRecord[]>>(
      `/b2c-stock-batches${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<B2cStockBatchRecord[]>(responseBody)
  },
  async getB2cStockBatch(batchId: string) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchDetail>>(
      `/b2c-stock-batches/${batchId}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<B2cStockBatchDetail>(responseBody)
  },
  async createB2cStockBatch(payload: B2cStockBatchCreateRequest) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchDetail>>(
      "/b2c-stock-batches",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<B2cStockBatchDetail>(responseBody)
  },
  async markB2cStockBatchPrinted(batchId: string) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchRecord>>(
      `/b2c-stock-batches/${batchId}/mark-printed`,
      {
        auth: "operator",
        body: {},
        method: "POST",
      }
    )

    return unwrapEnvelope<B2cStockBatchRecord>(responseBody)
  },
  async distributeB2cStockBatch(batchId: string) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchRecord>>(
      `/b2c-stock-batches/${batchId}/distribute`,
      {
        auth: "operator",
        body: {},
        method: "POST",
      }
    )

    return unwrapEnvelope<B2cStockBatchRecord>(responseBody)
  },
  async startB2cStockBatchDeleteOtp(payload: B2cStockBatchDeleteOtpStartRequest) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchDeleteOtpStartResponse>>(
      "/b2c-stock-batches/delete-otp/start",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<B2cStockBatchDeleteOtpStartResponse>(responseBody)
  },
  async deleteSelectedB2cStockBatches(payload: B2cStockBatchDeleteSelectedRequest) {
    const responseBody = await request<ApiEnvelope<B2cStockBatchDeleteSelectedResponse>>(
      "/b2c-stock-batches/delete-selected",
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<B2cStockBatchDeleteSelectedResponse>(responseBody)
  },
  async getOpsMonitoring(query: {
    limit?: number
    organizationId?: string
  } = {}) {
    const search = buildSearchParams({
      limit: query.limit ?? 18,
      organizationId: query.organizationId,
    })
    const responseBody = await request<OpsMonitoringSnapshot>(
      `/ops/monitoring${search ? `?${search}` : ""}`,
      {
        auth: "none",
      }
    )

    return responseBody
  },
  async getOpsSirenRoutingPreview(query: {
    gpsLatitude?: number | null
    gpsLongitude?: number | null
    organizationId?: string
    plantationId: string
    zoneName?: string | null
  }) {
    const search = buildSearchParams({
      gpsLatitude: query.gpsLatitude,
      gpsLongitude: query.gpsLongitude,
      organizationId: query.organizationId,
      plantationId: query.plantationId,
      zoneName: query.zoneName,
    })
    const responseBody = await request<ApiEnvelope<OpsSirenRoutingPreview>>(
      `/ops/siren-routing-preview${search ? `?${search}` : ""}`,
      {
        auth: "none",
      }
    )

    return unwrapEnvelope<OpsSirenRoutingPreview>(responseBody)
  },
  async acknowledgeOpsSirenActivation(activationId: string) {
    const responseBody = await request<ApiEnvelope<OpsMonitoringSirenActivation>>(
      `/ops/siren-activations/${encodeURIComponent(activationId)}/acknowledge`,
      {
        auth: "operator",
        body: {},
        method: "POST",
      }
    )

    return unwrapEnvelope<OpsMonitoringSirenActivation>(responseBody)
  },
  async retryOpsSirenActivation(activationId: string) {
    const responseBody = await request<ApiEnvelope<OpsMonitoringSirenActivation>>(
      `/ops/siren-activations/${encodeURIComponent(activationId)}/retry`,
      {
        auth: "operator",
        body: {},
        method: "POST",
      }
    )

    return unwrapEnvelope<OpsMonitoringSirenActivation>(responseBody)
  },
  async getExportPackages(query: {
    exportType?: string
    includeManifest?: boolean
    limit?: number
    organizationId?: string
    purposeLabel?: string
    recipientType?: string
    regionKey?: string
    sensitivityLevel?: string
  } = {}) {
    const search = buildSearchParams({
      exportType: query.exportType,
      includeManifest: query.includeManifest ?? true,
      limit: query.limit ?? 120,
      organizationId: query.organizationId,
      purposeLabel: query.purposeLabel,
      recipientType: query.recipientType,
      regionKey: query.regionKey,
      sensitivityLevel: query.sensitivityLevel,
    })
    const responseBody = await request<ApiEnvelope<IncidentExportRecord[]>>(
      `/ecosystem/exports${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<IncidentExportRecord[]>(responseBody)
  },
  async getExportDeliveryWorkspace(exportId: string) {
    return request<ExportDeliveryWorkspaceResponse>(
      `/ecosystem/exports/${exportId}/delivery-attempts`,
      {
        auth: "operator",
      }
    )
  },
  async getPartnerIntegrations(query: {
    organizationId?: string
    partnerKey?: string
    status?: string
  } = {}) {
    const search = buildSearchParams({
      organizationId: query.organizationId,
      partnerKey: query.partnerKey,
      status: query.status,
    })
    const responseBody = await request<ApiEnvelope<PartnerIntegrationRecord[]>>(
      `/ecosystem/partner-integrations${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PartnerIntegrationRecord[]>(responseBody)
  },
  async getPartnerDeliveries(query: {
    exportId?: string
    limit?: number
    organizationId?: string
    partnerKey?: string
    status?: string
  } = {}) {
    const search = buildSearchParams({
      exportId: query.exportId,
      limit: query.limit ?? 200,
      organizationId: query.organizationId,
      partnerKey: query.partnerKey,
      status: query.status,
    })
    const responseBody = await request<ApiEnvelope<PartnerDeliveryRecord[]>>(
      `/ecosystem/partner-deliveries${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<PartnerDeliveryRecord[]>(responseBody)
  },
  async createPartnerDelivery(
    exportId: string,
    payload: Record<string, unknown>
  ) {
    const responseBody = await request<
      ApiEnvelope<{
        attempt: PartnerDeliveryAttemptRecord | null
        delivery: PartnerDeliveryRecord
        rollout?: PartnerDeliveryRolloutContext | null
      }>
    >(`/ecosystem/exports/${exportId}/partner-deliveries`, {
      auth: "operator",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope(responseBody)
  },
  async getPartnerDeliveryAttempts(deliveryId: string) {
    return request<PartnerDeliveryAttemptLedgerResponse>(
      `/ecosystem/partner-deliveries/${deliveryId}/attempts`,
      {
        auth: "operator",
      }
    )
  },
  async getPartnerDeliveryCallbackEvents(deliveryId: string, limit = 30) {
    const search = buildSearchParams({ limit })

    return request<PartnerDeliveryCallbackEventResponse>(
      `/ecosystem/partner-deliveries/${deliveryId}/callback-events${search ? `?${search}` : ""}`,
      {
        auth: "operator",
      }
    )
  },
  async dispatchPartnerDeliveryAttempt(
    attemptId: string,
    payload: Record<string, unknown> = {}
  ) {
    const responseBody = await request<
      ApiEnvelope<{
        attempt: PartnerDeliveryAttemptRecord
        delivery: PartnerDeliveryRecord
      }>
    >(`/ecosystem/partner-delivery-attempts/${attemptId}/dispatch`, {
      auth: "operator",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope(responseBody)
  },
  async retryPartnerDelivery(
    deliveryId: string,
    payload: Record<string, unknown> = {}
  ) {
    const responseBody = await request<
      ApiEnvelope<{
        attempt: PartnerDeliveryAttemptRecord
        rollout?: PartnerDeliveryRolloutContext | null
      }>
    >(`/ecosystem/partner-deliveries/${deliveryId}/retry`, {
      auth: "operator",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope(responseBody)
  },
  async replayPartnerDelivery(
    deliveryId: string,
    payload: Record<string, unknown> = {}
  ) {
    const responseBody = await request<
      ApiEnvelope<{
        attempt: PartnerDeliveryAttemptRecord | null
        delivery: PartnerDeliveryRecord
        rollout?: PartnerDeliveryRolloutContext | null
      }>
    >(`/ecosystem/partner-deliveries/${deliveryId}/replay`, {
      auth: "operator",
      body: payload,
      method: "POST",
    })

    return unwrapEnvelope(responseBody)
  },
  async getIncidentFnol(incidentId: number) {
    const responseBody = await request<ApiEnvelope<IncidentFnolBundle>>(
      `/incidents/${incidentId}/fnol`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<IncidentFnolBundle>(responseBody)
  },
  async saveIncidentFnol(
    incidentId: number,
    payload: Record<string, unknown>
  ) {
    const responseBody = await request<ApiEnvelope<IncidentFnolBundle>>(
      `/incidents/${incidentId}/fnol`,
      {
        auth: "operator",
        body: payload,
        method: "PUT",
      }
    )

    return unwrapEnvelope<IncidentFnolBundle>(responseBody)
  },
  async prepareIncidentExport(
    incidentId: number,
    payload: Record<string, unknown>
  ) {
    const responseBody = await request<ApiEnvelope<IncidentExportPreparationResult>>(
      `/incidents/${incidentId}/export`,
      {
        auth: "operator",
        body: payload,
        method: "POST",
      }
    )

    return unwrapEnvelope<IncidentExportPreparationResult>(responseBody)
  },
  async getIncidentEvidenceBundle(incidentId: number) {
    const responseBody = await request<ApiEnvelope<IncidentEvidenceBundle>>(
      `/incidents/${incidentId}/evidence`,
      {
        auth: "operator",
      }
    )

    return unwrapEnvelope<IncidentEvidenceBundle>(responseBody)
  },
  async getAiPerformance() {
    const responseBody = await request<ApiEnvelope<AiPerformanceSnapshot>>(
      "/ai-insights/performance"
    )

    return unwrapEnvelope<AiPerformanceSnapshot>(responseBody)
  },
  async getAiJobs(limit = 10) {
    const search = buildSearchParams({ limit })
    const responseBody = await request<ApiEnvelope<AiJobRecord[]>>(
      `/ai-insights/jobs${search ? `?${search}` : ""}`
    )

    return unwrapEnvelope<AiJobRecord[]>(responseBody)
  },
  async getAiInsights(limit = 10) {
    const search = buildSearchParams({ limit })
    const responseBody = await request<ApiEnvelope<AiInsightRecord[]>>(
      `/ai-insights/insights${search ? `?${search}` : ""}`
    )

    return unwrapEnvelope<AiInsightRecord[]>(responseBody)
  },
  async getAiUrgentIncidents() {
    const responseBody = await request<
      ApiEnvelope<AiUrgentIncidentRecord[]> & {
        threshold?: number
      }
    >("/ai-insights/urgent")

    return responseBody
  },
  async getAiKeywords() {
    const responseBody = await request<
      ApiEnvelope<AiKeywordRecord[]> & {
        total_keywords?: number
      }
    >("/ai-insights/keywords")

    return responseBody
  },
  async getAiDisagreements() {
    const responseBody = await request<ApiEnvelope<AiDisagreementRecord[]>>(
      "/ai-insights/disagreements"
    )

    return unwrapEnvelope<AiDisagreementRecord[]>(responseBody)
  },
  createIncidentStream() {
    return new EventSource("/api/incidents/stream")
  },
}

export function parseOperatorIncidentStreamEvent(
  eventType: string,
  rawData: string
): OperatorIncidentStreamEvent | null {
  if (
    eventType !== "connected"
    && eventType !== "incident_created"
    && eventType !== "incident_updated"
  ) {
    return null
  }

  const parsed = JSON.parse(rawData) as Record<string, unknown>
  const payload = isPlainObject(parsed.data) ? parsed.data : parsed

  return {
    eventType,
    data: {
      createdAt:
        typeof payload.createdAt === "string" ? payload.createdAt : null,
      id: typeof payload.id === "number" ? payload.id : null,
      incidentStatus:
        typeof payload.incidentStatus === "string"
          ? payload.incidentStatus
          : null,
      locationDescription:
        typeof payload.locationDescription === "string"
          ? payload.locationDescription
          : null,
      reportKind:
        typeof payload.reportKind === "string" ? payload.reportKind : null,
      severity: typeof payload.severity === "string" ? payload.severity : null,
      stickerId:
        typeof payload.stickerId === "string" ? payload.stickerId : null,
      type: typeof payload.type === "string" ? payload.type : null,
    },
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function getApiFailure(error: unknown): ApiFailure | null {
  if (!(error instanceof ApiError) || !isPlainObject(error.responseBody)) {
    return null
  }

  return error.responseBody as ApiFailure
}
